import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown, Download, FileText, Landmark, Printer, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import {
  AccountCodeSelect,
  DEFAULT_PAGE_SIZE,
  Field,
  Panel,
  PaginationFooter,
  SelectField,
  SummaryCard,
  VatCodeSelect,
  calculateVatInclusiveAmounts,
  canonicalVatCodeValue,
  formatDate,
  formatDateTime,
  formatMoney,
  normalisePaginatedResponse,
  normalisePageSize,
  readSessionState,
  statusBadgeClass,
  vatCodeOptionsFromWorkspace,
  vatActiveForDate,
  writeSessionState,
} from "./shared";

const bankTabs = ["Reconciliation", "Bank Statements", "Account Transactions", "Audit Trail"];
const reconciliationStatuses = new Set(["unreconciled", "pending", "awaiting_match", "unmatched", "imported", "documentation_requested"]);
const creationActions = [
  "Create receipt",
  "Create payment",
  "Create transfer",
  "Create direct expense",
  "Create direct income",
];

function BankingWorkspace({ workspace, tab = "Dashboard", reloadWorkspace, busy, setHeaderContext }) {
  const banking = workspace.banking || {};
  const accounts = useMemo(() => workspace.accounts || [], [workspace.accounts]);
  const supportsBankingAccountFlag = useMemo(() => accounts.some((account) => "show_in_banking" in account || "banking_enabled" in account), [accounts]);
  const bankEnabledChartAccounts = useMemo(() => accounts.filter(isBankingEnabledAccount), [accounts]);
  const workspaceBankAccounts = useMemo(() => supportsBankingAccountFlag ? bankEnabledChartAccounts : banking.bank_accounts || [], [supportsBankingAccountFlag, bankEnabledChartAccounts, banking.bank_accounts]);
  const vatCodes = useMemo(() => vatCodeOptionsFromWorkspace(workspace), [workspace]);
  const usingBankingFallback = !supportsBankingAccountFlag && (banking.bank_accounts || []).length > 0;
  const postingAccounts = accounts.filter((account) => account.active && account.purpose !== "Bank Account" && String(account.account_type || "").toLowerCase() !== "bank");
  const suppliers = workspace.accounts_payable?.suppliers || [];
  const customers = workspace.accounts_receivable?.customers || [];
  const baseUrl = `/admin/accounting/clients/${workspace.client.id}`;
  const [analyticalOptions, setAnalyticalOptions] = useState({ locations: [], dimensions: [] });
  useEffect(() => {
    Promise.all([
      api.get(`${baseUrl}/locations?page=1&page_size=250&status=active`),
      api.get(`${baseUrl}/dimensions?page=1&page_size=250&status=active`),
    ]).then(([locations, dimensions]) => setAnalyticalOptions({
      locations: normalisePaginatedResponse(locations.data).rows,
      dimensions: normalisePaginatedResponse(dimensions.data).rows,
    })).catch(() => setAnalyticalOptions({ locations: [], dimensions: [] }));
  }, [baseUrl]);
  const navigationStorageKey = `epos-native-accounting.banking-view.${workspace.client.id}.v1`;
  const initialNavigation = useMemo(() => readSessionState(navigationStorageKey, {}), [navigationStorageKey]);

  const [saving, setSaving] = useState(false);
  const [bankAccountsData, setBankAccountsData] = useState(workspaceBankAccounts);
  const bankAccounts = bankAccountsData.length ? bankAccountsData : workspaceBankAccounts;
  const activeBankAccounts = bankAccounts.filter((account) => account.active !== false);
  const defaultBankId = banking.settings?.default_bank_account_id || activeBankAccounts[0]?.id || bankAccounts[0]?.id || "";
  const [selectedBankId, setSelectedBankId] = useState(() => initialNavigation.selectedBankId || "");
  const [bankTab, setBankTab] = useState(() => bankTabs.includes(initialNavigation.bankTab) ? initialNavigation.bankTab : "Reconciliation");
  const [, setSelectedTransaction] = useState(null);
  const [selectedImportId, setSelectedImportId] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [filters, setFilters] = useState({ bank_account_id: "", module: "", type: "", contact: "", date_from: "", date_to: "", status: "", search: "" });
  const [reconciliationPage, setReconciliationPage] = useState(1);
  const [reconciliationPageSize, setReconciliationPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [reconciliationData, setReconciliationData] = useState(() => normalisePaginatedResponse({ rows: [], page_size: DEFAULT_PAGE_SIZE }));
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [reconciliationError, setReconciliationError] = useState("");
  const [statementPage, setStatementPage] = useState(1);
  const [statementPageSize, setStatementPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [statementStatusFilter, setStatementStatusFilter] = useState("all");
  const [statementData, setStatementData] = useState(() => normalisePaginatedResponse({ rows: [], page_size: DEFAULT_PAGE_SIZE }));
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementError, setStatementError] = useState("");
  const [accountTxPage, setAccountTxPage] = useState(1);
  const [accountTxPageSize, setAccountTxPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [accountTxData, setAccountTxData] = useState(() => normalisePaginatedResponse({ rows: [], page_size: DEFAULT_PAGE_SIZE }));
  const [accountTxLoading, setAccountTxLoading] = useState(false);
  const [accountTxError, setAccountTxError] = useState("");
  const [bankingRefreshKey, setBankingRefreshKey] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditData, setAuditData] = useState(() => normalisePaginatedResponse({ page_size: DEFAULT_PAGE_SIZE }));
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [transactionForm, setTransactionForm] = useState({ bank_account_id: defaultBankId, transaction_date: "", description: "", reference: "", transaction_type: "manual_entry", money_in: "", money_out: "" });
  const [directEntry, setDirectEntry] = useState(null);
  const [settingsForm, setSettingsForm] = useState(banking.settings || {});

  useEffect(() => {
    setSettingsForm(banking.settings || {});
  }, [banking.settings]);

  useEffect(() => {
    writeSessionState(navigationStorageKey, { selectedBankId, bankTab });
  }, [bankTab, navigationStorageKey, selectedBankId]);

  useEffect(() => {
    if (defaultBankId) {
      setTransactionForm((current) => ({ ...current, bank_account_id: current.bank_account_id || defaultBankId }));
    }
  }, [defaultBankId]);

  useEffect(() => {
    if (selectedBankId && !bankAccounts.some((account) => bankAccountMatchesValue(account, selectedBankId))) {
      setSelectedBankId("");
      setSelectedTransaction(null);
    }
  }, [bankAccounts, selectedBankId]);

  const selectedBank = bankAccounts.find((account) => bankAccountMatchesValue(account, selectedBankId)) || null;
  const bankImports = statementData.imports || [];
  const bankTransactions = statementData.rows || [];
  const unreconciled = reconciliationData.rows || [];
  const outstandingAccountTransactions = accountTxData.rows || [];
  const visibleAccountTransactions = accountTxData.rows || [];
  const selectedImport = bankImports.find((item) => item.id === selectedImportId) || null;
  const selectedImportLines = selectedImport ? importLines(selectedImport, bankTransactions) : [];

  useEffect(() => {
    const target = {
      Reconciliation: "Reconciliation",
      Transactions: "Account Transactions",
      Cashbook: "Account Transactions",
      "Imported Statements": "Bank Statements",
    }[tab];
    if (target && defaultBankId) {
      setSelectedBankId(defaultBankId);
      setBankTab(target);
      return;
    }
    if (["Dashboard", "Bank Accounts", "Settings"].includes(tab)) {
      setSelectedBankId("");
      setSelectedTransaction(null);
    }
  }, [defaultBankId, tab]);

  useEffect(() => {
    if (!setHeaderContext) return undefined;
    if (!selectedBank) {
      setHeaderContext(null);
      return undefined;
    }
    setHeaderContext({
      backLabel: "Back to bank accounts",
      onBack: () => {
        setSelectedBankId("");
        setSelectedTransaction(null);
      },
      title: bankName(selectedBank) || "Bank account",
      subtitle: `${[selectedBank.bank_name, selectedBank.sort_code, selectedBank.account_number].filter(Boolean).join(" - ") || "Chart of Accounts bank-enabled account"} - Nominal ${bankCode(selectedBank) || "-"}`,
      tabs: bankTabs,
      activeTab: bankTab,
      onTabChange: (item) => {
        setBankTab(item);
        setSelectedTransaction(null);
        if (item === "Account Transactions") setFilters((current) => ({ ...current, bank_account_id: "" }));
      },
      variant: "banking",
      actionMenu: creationActions.map((action) => ({
        label: action,
        onClick: () => {
          if (action === "Create direct expense" || action === "Create direct income") {
            const direction = action === "Create direct income" ? "income" : "expense";
            setDirectEntry({ direction, transaction_date: new Date().toISOString().slice(0, 10), description: "", reference: "", amount: "", nominal_account_code: "", vat_code: "NO VAT" });
            return;
          }
          showBackendRequired(action);
        },
      })),
      actions: [
        {
          label: "Import Statement",
          icon: false,
          onClick: () => setBankTab("Bank Statements"),
        },
        {
          label: "Refresh",
          icon: false,
          onClick: () => {
            reloadWorkspace?.();
            setBankingRefreshKey((key) => key + 1);
          },
        },
        {
          label: "Link Bank",
          icon: false,
          onClick: () => toast.info("Link Bank will be available later in the project."),
        },
      ],
    });
    return () => setHeaderContext(null);
    // Header actions intentionally use current Banking handlers without rebuilding on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBank, bankTab, setHeaderContext]);

  useEffect(() => {
    setBankAccountsData(workspaceBankAccounts);
  }, [workspaceBankAccounts]);

  useEffect(() => {
    let cancelled = false;
    async function loadBankAccounts() {
      try {
        const { data } = await api.get(`${baseUrl}/banking/accounts`);
        const rows = Array.isArray(data?.rows) ? data.rows : Array.isArray(data?.accounts) ? data.accounts : Array.isArray(data) ? data : [];
        if (!cancelled && rows.length) setBankAccountsData(rows);
      } catch {
        if (!cancelled) setBankAccountsData(workspaceBankAccounts);
      }
    }
    loadBankAccounts();
    return () => {
      cancelled = true;
    };
  }, [baseUrl, workspaceBankAccounts]);

  useEffect(() => {
    setReconciliationPage(1);
    setStatementPage(1);
    setAccountTxPage(1);
    setSelectedImportId("");
  }, [selectedBankId, bankTab, statementStatusFilter, filters.bank_account_id, filters.module, filters.type, filters.contact, filters.date_from, filters.date_to, filters.status, filters.search]);

  useEffect(() => {
    if (!selectedBankId || bankTab !== "Reconciliation") return;
    let cancelled = false;
    const params = new URLSearchParams({
      page: String(reconciliationPage),
      page_size: String(reconciliationPageSize),
    });
    setReconciliationLoading(true);
    setReconciliationError("");
    api.get(`${baseUrl}/banking/accounts/${selectedBankId}/reconciliation?${params.toString()}`)
      .then(({ data }) => {
        if (!cancelled) setReconciliationData(normalisePaginatedResponse(data, reconciliationPageSize));
      })
      .catch((error) => {
        if (!cancelled) {
          setReconciliationData(normalisePaginatedResponse({ rows: [], page: reconciliationPage, page_size: reconciliationPageSize }));
          setReconciliationError(formatApiError(error));
        }
      })
      .finally(() => {
        if (!cancelled) setReconciliationLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bankingRefreshKey, baseUrl, bankTab, reconciliationPage, reconciliationPageSize, selectedBankId]);

  useEffect(() => {
    if (!selectedBankId || bankTab !== "Bank Statements") return;
    let cancelled = false;
    const params = new URLSearchParams({
      page: String(statementPage),
      page_size: String(statementPageSize),
      status: statementStatusFilter || "all",
    });
    if (selectedImportId) params.set("import_id", selectedImportId);
    setStatementLoading(true);
    setStatementError("");
    api.get(`${baseUrl}/banking/accounts/${selectedBankId}/statements?${params.toString()}`)
      .then(({ data }) => {
        if (!cancelled) setStatementData(normalisePaginatedResponse(data, statementPageSize));
      })
      .catch((error) => {
        if (!cancelled) {
          setStatementData(normalisePaginatedResponse({ rows: [], page: statementPage, page_size: statementPageSize }));
          setStatementError(formatApiError(error));
        }
      })
      .finally(() => {
        if (!cancelled) setStatementLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bankingRefreshKey, baseUrl, bankTab, selectedBankId, selectedImportId, statementPage, statementPageSize, statementStatusFilter]);

  useEffect(() => {
    if (!selectedBankId || !["Account Transactions", "Reconciliation"].includes(bankTab)) return;
    let cancelled = false;
    const params = new URLSearchParams({
      page: String(bankTab === "Reconciliation" ? 1 : accountTxPage),
      page_size: String(bankTab === "Reconciliation" ? 250 : accountTxPageSize),
      direction: "all",
    });
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    setAccountTxLoading(true);
    setAccountTxError("");
    api.get(`${baseUrl}/banking/account-transactions?${params.toString()}`)
      .then(({ data }) => {
        if (!cancelled) setAccountTxData(normalisePaginatedResponse(data, accountTxPageSize));
      })
      .catch((error) => {
        if (!cancelled) {
          setAccountTxData(normalisePaginatedResponse({ rows: [], page: accountTxPage, page_size: accountTxPageSize }));
          setAccountTxError(formatApiError(error));
        }
      })
      .finally(() => {
        if (!cancelled) setAccountTxLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountTxPage, accountTxPageSize, bankTab, bankingRefreshKey, baseUrl, filters, selectedBankId]);

  useEffect(() => { setAuditPage(1); }, [selectedBankId, auditSearch, auditPageSize]);
  useEffect(() => {
    if (bankTab !== "Audit Trail" || !selectedBankId) return;
    let cancelled = false;
    const params = new URLSearchParams({ page: String(auditPage), page_size: String(auditPageSize) });
    if (auditSearch) params.set("search", auditSearch);
    setAuditLoading(true); setAuditError("");
    api.get(`${baseUrl}/banking/accounts/${selectedBankId}/audit-trail?${params.toString()}`)
      .then(({ data }) => { if (!cancelled) setAuditData(normalisePaginatedResponse(data, auditPageSize)); })
      .catch((error) => { if (!cancelled) setAuditError(formatApiError(error)); })
      .finally(() => { if (!cancelled) setAuditLoading(false); });
    return () => { cancelled = true; };
  }, [baseUrl, bankTab, selectedBankId, auditPage, auditPageSize, auditSearch]);

  async function run(action, success) {
    setSaving(true);
    try {
      await action();
      toast.success(success);
      await reloadWorkspace();
      setBankingRefreshKey((key) => key + 1);
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function createTransaction(e) {
    e.preventDefault();
    if (!transactionForm.money_in && !transactionForm.money_out) return toast.error("Enter money in or money out");
    const saved = await run(async () => api.post(`${baseUrl}/bank-transactions`, transactionForm), "Bank transaction added");
    if (saved) setTransactionForm((current) => ({ ...current, transaction_date: "", description: "", reference: "", money_in: "", money_out: "" }));
  }

  async function createDirectEntry(e) {
    e.preventDefault();
    if (!directEntry?.transaction_date) return toast.error("Choose a transaction date");
    if (!directEntry?.description?.trim()) return toast.error("Enter a description");
    if (!directEntry?.nominal_account_code) return toast.error("Choose a posting account");
    const amount = Number(directEntry?.amount || 0);
    if (!(amount > 0)) return toast.error("Enter an amount greater than zero");
    const canonicalVat = canonicalVatCodeValue(directEntry.vat_code, vatCodes) || "NO VAT";
    const calculated = calculateVatInclusiveAmounts(amount, canonicalVat, vatCodes) || { net: amount.toFixed(2), vat: "0.00" };
    const isIncome = directEntry.direction === "income";
    setSaving(true);
    try {
      const { data: created } = await api.post(`${baseUrl}/bank-transactions`, {
        bank_account_id: selectedBank?.id || selectedBankId,
        bank_account_code: bankCode(selectedBank),
        transaction_date: directEntry.transaction_date,
        description: directEntry.description.trim(),
        reference: directEntry.reference?.trim() || directEntry.description.trim(),
        transaction_type: isIncome ? "direct_income" : "direct_expense",
        money_in: isIncome ? amount.toFixed(2) : "0.00",
        money_out: isIncome ? "0.00" : amount.toFixed(2),
      });
      const transactionId = created?.id || created?.bank_transaction?.id || created?.transaction?.id;
      if (!transactionId) throw new Error("The bank transaction was created without an identifier.");
      await api.post(`${baseUrl}/bank-transactions/${transactionId}/${isIncome ? "create-income" : "create-expense"}`, {
        description: directEntry.description.trim(), reference: directEntry.reference?.trim() || directEntry.description.trim(), nominal_account_code: directEntry.nominal_account_code,
        vat_code: canonicalVat, net_amount: calculated.net, vat_amount: calculated.vat, gross_amount: amount.toFixed(2),
      });
      toast.success(isIncome ? "Direct income posted" : "Direct expense posted");
      setDirectEntry(null);
      await reloadWorkspace?.();
      setBankingRefreshKey((key) => key + 1);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSaving(false); }
  }

  async function importStatement(e) {
    e.preventDefault();
    if (!importFile) return toast.error("Choose a CSV file to import");
    const bankId = selectedBank?.id || transactionForm.bank_account_id || defaultBankId;
    const data = new FormData();
    data.append("file", importFile);
    data.append("bank_account_id", bankId);
    data.append("bank_account_code", bankCode(bankAccounts.find((account) => account.id === bankId)) || "1200");
    setSaving(true);
    try {
      const { data: response } = await api.post(`${baseUrl}/bank-transactions/import`, data);
      const result = importResultCounts(response);
      showImportResult(result);
      await reloadWorkspace();
      setBankingRefreshKey((key) => key + 1);
      setBankTab("Bank Statements");
      if (result.imported > 0) setImportFile(null);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  }

  async function downloadStatementTemplate() {
    if (!selectedBank?.id) return toast.error("Select the bank account first");
    setSaving(true);
    try {
      const response = await api.get(`${baseUrl}/banking/accounts/${selectedBank.id}/statement-import-template`, { responseType: "blob" });
      const disposition = response.headers?.["content-disposition"] || "";
      const filename = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)?.[1] || `bank-statement-template-${bankCode(selectedBank) || "bank"}.csv`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = decodeURIComponent(filename.replaceAll('"', ""));
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Template downloaded for ${selectedBank.account_name || selectedBank.name || bankCode(selectedBank)}`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  }

  async function reconcileToAccount(transaction, description, accountCode = "", extra = {}) {
    if (!accountCode) return toast.error("Choose a posting account");
    await run(async () => api.post(`${baseUrl}/bank-transactions/${transaction.id}/reconcile`, { account_code: accountCode, description: description || transaction.description, reference: transaction.reference, ...extra }), "Transaction reconciled");
    setSelectedTransaction(null);
  }

  async function reconcileSplitToAccounts(transaction, splitLines) {
    setSaving(true);
    try {
      await api.post(`${baseUrl}/bank-transactions/${transaction.id}/reconcile/split`, { lines: splitLines, reference: transaction.reference });
      toast.success("Split nominal reconciliation posted");
      await reloadWorkspace();
      setBankingRefreshKey((key) => key + 1);
      setSelectedTransaction(null);
    } catch (e) {
      const status = e?.response?.status || e?.status;
      if (status === 404 || status === 405 || status === 501) {
        toast.error("Backend endpoint required: split nominal reconciliation.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  }

  async function matchSuggestion(transaction, suggestion) {
    const suggestions = Array.isArray(suggestion) ? suggestion : null;
    await run(
      async () => api.post(`${baseUrl}/bank-transactions/${transaction.id}/match`, suggestions ? { selected_transactions: suggestions } : { match_type: suggestion.type || suggestion.record_type, record_id: suggestion.record_id || suggestion.id, account_transaction_id: suggestion.account_transaction_id, confidence: suggestion.confidence }),
      "Suggested match posted"
    );
    setSelectedTransaction(null);
  }

  async function createSupplierOrCustomerTransaction(transaction, payload, attachment = null) {
    const moneyIn = Number(transaction.money_in || 0) > 0;
    const endpoint = moneyIn ? "create-customer-receipt" : "create-supplier-payment";
    const createsDocument = Boolean(payload.nominal_account_code);
    const saved = await run(
      async () => {
        let requestPayload = payload;
        if (attachment) {
          const form = new FormData();
          form.append("file", attachment);
          const { data } = await api.post(`${baseUrl}/bank-transactions/${transaction.id}/supporting-document`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          requestPayload = {
            ...payload,
            attachment_path: data?.attachment_path || "",
            original_filename: data?.original_filename || attachment.name,
          };
        }
        return api.post(`${baseUrl}/bank-transactions/${transaction.id}/${endpoint}`, requestPayload);
      },
      createsDocument
        ? moneyIn ? "Sales item and customer receipt posted" : "Supplier expense and payment posted"
        : moneyIn ? "Customer receipt posted on account" : "Supplier payment posted on account"
    );
    if (saved) setSelectedTransaction(null);
    return saved;
  }

  async function ignoreTransaction(transaction, reason = "") {
    await run(async () => api.post(`${baseUrl}/bank-transactions/${transaction.id}/ignore`, { reason }), "Transaction excluded");
    setSelectedTransaction(null);
  }

  async function sendToClientOutstandingItems(transaction, payload = {}) {
    setSaving(true);
    try {
      await api.post(`${baseUrl}/bank-transactions/${transaction.id}/send-to-client-outstanding-items`, payload);
      toast.success("Sent to client outstanding items");
      await reloadWorkspace();
      setBankingRefreshKey((key) => key + 1);
      setSelectedTransaction(null);
    } catch (e) {
      const status = e?.response?.status || e?.status;
      if (status === 404 || status === 405 || status === 501) {
        toast.error("Backend endpoint required: send bank transaction to client outstanding items.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  }

  async function runBulkBankAction(kind, rows, backendRequiredMessage) {
    setSaving(true);
    try {
      const ids = rows.map(rowId).filter(Boolean);
      const endpoints = {
        delete: `${baseUrl}/bank-transactions/bulk-delete`,
        exclude: `${baseUrl}/bank-transactions/bulk-exclude`,
        send: `${baseUrl}/bank-transactions/bulk-send-to-client-outstanding-items`,
        unreconcile: `${baseUrl}/bank-transactions/bulk-unreconcile`,
      };
      await api.post(endpoints[kind], { ids, transactions: rows.map(bankTransactionIdentity) });
      toast.success("Bulk action completed");
      await reloadWorkspace();
      setBankingRefreshKey((key) => key + 1);
      return { ok: true };
    } catch (e) {
      const status = e?.response?.status || e?.status;
      if ([404, 405, 501].includes(status)) return { ok: false, message: backendRequiredMessage };
      const message = formatApiError(e);
      toast.error(message);
      return { ok: false, message };
    } finally {
      setSaving(false);
    }
  }

  async function bulkDeleteAccountTransactions(rows) {
    setSaving(true);
    try {
      await api.post(`${baseUrl}/account-transactions/bulk-delete`, { transactions: rows.map(accountTransactionDeleteIdentity) });
      toast.success("Selected accounting transactions deleted");
      await reloadWorkspace();
      setBankingRefreshKey((key) => key + 1);
      return { ok: true };
    } catch (e) {
      const status = e?.response?.status || e?.status;
      if ([404, 405, 501].includes(status)) return { ok: false, message: "Backend endpoint required: bulk delete account transactions." };
      const message = formatApiError(e);
      toast.error(message);
      return { ok: false, message };
    } finally {
      setSaving(false);
    }
  }

  async function saveSettings(e) {
    e.preventDefault();
    await run(async () => api.put(`${baseUrl}/bank/settings`, settingsForm), "Bank settings saved");
  }

  function openBank(account) {
    setSelectedBankId(bankCode(account) || account.account_id || account.id);
    setBankTab("Reconciliation");
    setSelectedTransaction(null);
    setTransactionForm((current) => ({ ...current, bank_account_id: account.bank_account_id || account.id }));
  }

  function showBackendRequired(action) {
    toast.info(`Backend endpoint required for: ${action}.`);
  }

  if (tab === "Settings" && !selectedBank) {
    return (
      <Panel title="Banking settings">
        <form onSubmit={saveSettings} className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <BankAccountSelect label="Default bank account" bankAccounts={bankAccounts} value={settingsForm.default_bank_account_id} onChange={(value) => setSettingsForm((current) => ({ ...current, default_bank_account_id: value }))} />
          <AccountCodeSelect label="Default transfer account" accounts={accounts} value={settingsForm.default_transfer_account} onChange={(value) => setSettingsForm((current) => ({ ...current, default_transfer_account: value }))} />
          <AccountCodeSelect label="Bank charges account" accounts={postingAccounts} value={settingsForm.default_bank_charges_account} onChange={(value) => setSettingsForm((current) => ({ ...current, default_bank_charges_account: value }))} />
          <AccountCodeSelect label="Interest account" accounts={postingAccounts} value={settingsForm.default_interest_account} onChange={(value) => setSettingsForm((current) => ({ ...current, default_interest_account: value }))} />
          <AccountCodeSelect label="Suspense account" accounts={accounts} value={settingsForm.default_suspense_account} onChange={(value) => setSettingsForm((current) => ({ ...current, default_suspense_account: value }))} />
          <Field label="Statement prefix" value={settingsForm.statement_number_prefix} onChange={(value) => setSettingsForm((current) => ({ ...current, statement_number_prefix: value }))} />
          <Field label="Auto-match threshold" value={settingsForm.automatic_matching_threshold} onChange={(value) => setSettingsForm((current) => ({ ...current, automatic_matching_threshold: value }))} />
          <label className="mt-6 flex items-center gap-2 text-sm font-semibold text-stone-700"><input type="checkbox" checked={settingsForm.duplicate_detection !== false} onChange={(e) => setSettingsForm((current) => ({ ...current, duplicate_detection: e.target.checked }))} /> Duplicate detection</label>
          <Button disabled={busy || saving} className="md:col-span-2 xl:col-span-4" style={{ background: "var(--brand)" }}>Save banking settings</Button>
        </form>
      </Panel>
    );
  }

  if (!selectedBank) {
    return (
      <div className="space-y-4">
        {usingBankingFallback ? <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">Backend required: persist banking_enabled/show_in_banking on accounting accounts and use it to populate Banking cards. Showing existing Banking records for now.</div> : null}
        <Panel title="Bank accounts">
          {bankAccounts.length ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {bankAccounts.map((account) => (
                <BankAccountCard
                  key={account.id || bankCode(account)}
                  account={account}
                  unreconciledCount={account.unreconciled_count ?? account.unreconciled_items ?? account.summary?.unreconciled_count ?? 0}
                  lastImport={account.last_import_date || account.last_import_at || account.summary?.last_import_date}
                  onOpen={() => openBank(account)}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-stone-300 bg-stone-50 p-10 text-center">
              <FileText className="mx-auto h-8 w-8 text-stone-400" />
              <div className="mt-3 font-semibold text-stone-900">No bank accounts are enabled for Banking.</div>
              <p className="mt-1 text-sm text-stone-500">Enable a bank account from Chart of Accounts.</p>
            </div>
          )}
        </Panel>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {directEntry ? (
        <Panel title={directEntry.direction === "income" ? "Create direct income" : "Create direct expense"}>
          <form onSubmit={createDirectEntry} className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <Field label="Date" type="date" value={directEntry.transaction_date} onChange={(value) => setDirectEntry((current) => ({ ...current, transaction_date: value }))} />
            <Field label="Description" value={directEntry.description} onChange={(value) => setDirectEntry((current) => ({ ...current, description: value }))} />
            <Field label="Reference" value={directEntry.reference} onChange={(value) => setDirectEntry((current) => ({ ...current, reference: value }))} />
            <Field label="Amount" type="number" value={directEntry.amount} onChange={(value) => setDirectEntry((current) => ({ ...current, amount: value }))} />
            <AccountCodeSelect label="Posting account" accounts={postingAccounts} value={directEntry.nominal_account_code} onChange={(value) => setDirectEntry((current) => ({ ...current, nominal_account_code: value }))} />
            <VatCodeSelect label="VAT code" value={directEntry.vat_code} options={vatCodes} onChange={(value) => setDirectEntry((current) => ({ ...current, vat_code: value }))} />
            <div className="flex items-end gap-2 md:col-span-2"><Button type="submit" disabled={saving || busy} style={{ background: "var(--brand)" }}>Post transaction</Button><Button type="button" variant="outline" disabled={saving} onClick={() => setDirectEntry(null)}>Cancel</Button></div>
          </form>
        </Panel>
      ) : null}
      <div className="grid gap-3 md:grid-cols-4">
        <SummaryCard label="Current balance" value={formatMoney(selectedBank.current_balance)} tone="emerald" />
        <SummaryCard label="Reconciled balance" value={formatMoney(selectedBank.reconciled_balance)} tone="blue" />
        <SummaryCard label="Unreconciled items" value={reconciliationData.summary.unreconciled_count ?? selectedBank.unreconciled_count ?? selectedBank.unreconciled_items ?? reconciliationData.total_rows ?? 0} tone="amber" />
        <SummaryCard label="Last import" value={formatDate(statementData.summary.last_import_date || selectedBank.last_import_date || selectedBank.last_import_at)} tone="stone" />
      </div>

      {bankTab === "Reconciliation" ? (
        <ReconciliationTab
          transactions={unreconciled}
          outstandingTransactions={outstandingAccountTransactions}
          totalRows={reconciliationData.total_rows}
          totalPages={reconciliationData.total_pages}
          page={reconciliationPage}
          pageSize={reconciliationPageSize}
          loading={reconciliationLoading}
          error={reconciliationError}
          onPageChange={setReconciliationPage}
          onPageSizeChange={(size) => {
            setReconciliationPageSize(normalisePageSize(size));
            setReconciliationPage(1);
          }}
          bankAccounts={bankAccounts}
          selectedBankId={selectedBankId}
          postingAccounts={postingAccounts}
          vatCodes={vatCodes}
          analyticalOptions={analyticalOptions}
          vatStatus={workspace}
          suppliers={suppliers}
          customers={customers}
          matchSuggestion={matchSuggestion}
          createSupplierOrCustomerTransaction={createSupplierOrCustomerTransaction}
          reconcileToAccount={reconcileToAccount}
          reconcileSplitToAccounts={reconcileSplitToAccounts}
          ignoreTransaction={ignoreTransaction}
          sendToClientOutstandingItems={sendToClientOutstandingItems}
          bulkAction={runBulkBankAction}
          saving={saving || busy}
        />
      ) : null}

      {bankTab === "Bank Statements" ? (
        <BankStatementsTab
          imports={bankImports}
          statementLines={bankTransactions}
          selectedBank={selectedBank}
          selectedImportId={selectedImportId}
          setSelectedImportId={setSelectedImportId}
          selectedImportLines={selectedImportLines}
          statusFilter={statementStatusFilter}
          setStatusFilter={(value) => {
            setStatementStatusFilter(value || "all");
            setStatementPage(1);
          }}
          totalRows={statementData.total_rows}
          totalPages={statementData.total_pages}
          page={statementPage}
          pageSize={statementPageSize}
          loading={statementLoading}
          error={statementError}
          onPageChange={setStatementPage}
          onPageSizeChange={(size) => {
            setStatementPageSize(normalisePageSize(size));
            setStatementPage(1);
          }}
          importFile={importFile}
          setImportFile={setImportFile}
          importStatement={importStatement}
          downloadStatementTemplate={downloadStatementTemplate}
          bulkAction={runBulkBankAction}
          busy={busy || saving}
        />
      ) : null}

      {bankTab === "Account Transactions" ? (
        <AccountTransactionsTab
          filters={filters}
          setFilters={setFilters}
          transactions={visibleAccountTransactions}
          allTransactions={outstandingAccountTransactions}
          totalRows={accountTxData.total_rows}
          totalPages={accountTxData.total_pages}
          page={accountTxPage}
          pageSize={accountTxPageSize}
          loading={accountTxLoading}
          error={accountTxError}
          onPageChange={setAccountTxPage}
          onPageSizeChange={(size) => {
            setAccountTxPageSize(normalisePageSize(size));
            setAccountTxPage(1);
          }}
          bankAccounts={bankAccounts}
          bulkDelete={bulkDeleteAccountTransactions}
          busy={busy || saving}
          exportTransactions={() => exportRows(visibleAccountTransactions, "all-outstanding-account-transactions.csv")}
        />
      ) : null}
      {bankTab === "Audit Trail" ? <BankAuditTrail data={auditData} loading={auditLoading} error={auditError} search={auditSearch} setSearch={setAuditSearch} setPage={setAuditPage} setPageSize={setAuditPageSize} /> : null}
    </div>
  );
}

function BankAuditTrail({ data, loading, error, search, setSearch, setPage, setPageSize }) {
  return <Panel title="Bank account audit trail"><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search audit trail" className="mb-3 h-9 max-w-lg" />{error ? <p className="py-8 text-center text-sm text-red-700">{error}</p> : loading && !data.rows.length ? <p className="py-8 text-center text-sm text-stone-500">Loading audit trail...</p> : <div className="overflow-auto"><table className="min-w-full text-left text-sm"><thead className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500"><tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">User</th><th className="px-3 py-2">Action</th><th className="px-3 py-2">Details</th></tr></thead><tbody>{data.rows.map((row) => <tr key={row.id} className="border-t border-stone-100"><td className="px-3 py-2">{formatDateTime(row.created_at)}</td><td className="px-3 py-2">{row.actor_id || "System"}</td><td className="px-3 py-2 font-medium">{row.action}</td><td className="px-3 py-2 text-stone-600">{row.module || row.entity_type || "Banking"}</td></tr>)}</tbody></table></div>}<PaginationFooter page={data.page} pageSize={data.page_size} totalRows={data.total_rows} totalPages={data.total_pages} onPageChange={setPage} onPageSizeChange={setPageSize} disabled={loading} /></Panel>;
}

function BankAccountCard({ account, unreconciledCount, lastImport, onOpen }) {
  return (
    <button type="button" onClick={onOpen} className="group flex min-h-[220px] flex-col rounded-xl border border-stone-200 bg-white p-4 text-left shadow-[0_3px_12px_rgba(28,25,23,0.07)] transition duration-150 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_10px_26px_rgba(6,78,59,0.13)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
            <Landmark className="h-6 w-6" />
          </span>
          <div className="min-w-0 pt-0.5">
            <h4 className="truncate font-display text-base font-bold leading-tight text-stone-950">{bankName(account) || "Unnamed bank account"}</h4>
            <p className="mt-1 truncate text-xs text-stone-500">{account.bank_name || "Bank"} {account.sort_code || account.account_number ? `- ${[account.sort_code, account.account_number].filter(Boolean).join(" / ")}` : ""}</p>
            <p className="mt-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Bank account</p>
          </div>
        </div>
        <Badge className={statusBadgeClass(account.active === false ? "inactive" : "active")}>{account.active === false ? "Inactive" : "Active"}</Badge>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-stone-200 pt-3">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Current balance</div>
          <div className="mt-1 truncate font-display text-base font-bold text-emerald-800">{formatMoney(account.current_balance)}</div>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Reconciled</div>
          <div className="mt-1 truncate font-display text-base font-bold text-sky-800">{formatMoney(account.reconciled_balance)}</div>
        </div>
      </div>
      <div className="mt-auto grid gap-1 border-t border-stone-100 pt-3 text-xs text-stone-500">
        <div className="flex items-center justify-between gap-2"><span>Nominal account</span><span className="font-medium text-stone-700">{bankCode(account) || "-"}</span></div>
        <div className="flex items-center justify-between gap-2"><span>Unreconciled</span><span className="font-medium text-stone-700">{unreconciledCount}</span></div>
        <div className="flex items-center justify-between gap-2"><span>Last statement/import</span><span className="font-medium text-stone-700">{lastImport ? formatDate(lastImport) : "-"}</span></div>
      </div>
    </button>
  );
}

function ReconciliationTab({ transactions, outstandingTransactions, totalRows, totalPages, page, pageSize, loading, error, onPageChange, onPageSizeChange, bankAccounts, selectedBankId, postingAccounts, vatCodes, analyticalOptions, vatStatus, suppliers, customers, matchSuggestion, createSupplierOrCustomerTransaction, reconcileToAccount, reconcileSplitToAccounts, ignoreTransaction, sendToClientOutstandingItems, bulkAction, saving }) {
  const [expanded, setExpanded] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMessage, setBulkMessage] = useState("");
  const activeKey = expanded ? `${expanded.id}-${expanded.action}` : "";
  const pagedTransactions = transactions;
  const pagedTransactionIds = useMemo(() => pagedTransactions.map(rowId).filter(Boolean), [pagedTransactions]);
  const selectedVisibleRows = selectedRowsFromIds(pagedTransactions, selectedIds);
  const allVisibleSelected = pagedTransactionIds.length > 0 && pagedTransactionIds.every((id) => selectedIds.includes(id));

  useEffect(() => {
    setSelectedIds([]);
    setBulkMessage("");
    setExpanded(null);
  }, [transactions]);

  function toggle(transaction, action) {
    const key = `${transaction.id}-${action}`;
    if (action === "send" && isSentToClient(transaction) && !window.confirm("This bank transaction has already been sent to the client. Send again?")) return;
    setExpanded(activeKey === key ? null : { id: transaction.id, action });
  }

  function toggleSelected(transaction) {
    const id = rowId(transaction);
    setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  function toggleAll() {
    setSelectedIds((current) => {
      if (allVisibleSelected) return current.filter((id) => !pagedTransactionIds.includes(id));
      return Array.from(new Set([...current, ...pagedTransactionIds]));
    });
  }

  async function runBulk(kind, message) {
    setBulkMessage("");
    const result = await bulkAction(kind, selectedVisibleRows, message);
    if (result?.ok) setSelectedIds([]);
    if (result?.message) setBulkMessage(result.message);
  }

  return (
    <Panel title="Unreconciled statement lines">
      <BulkActionBar selectedCount={selectedVisibleRows.length} onClear={() => setSelectedIds([])} message={bulkMessage}>
        <Button type="button" size="sm" variant="outline" disabled={!selectedVisibleRows.length || saving} onClick={() => runBulk("delete", "Backend endpoint required: bulk delete bank statement lines.")}>Delete</Button>
        <Button type="button" size="sm" variant="outline" disabled={!selectedVisibleRows.length || saving} onClick={() => runBulk("exclude", "Backend endpoint required: bulk exclude bank statement lines.")}>Exclude</Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!selectedVisibleRows.length || saving}
          onClick={() => {
            if (selectedVisibleRows.some(isSentToClient) && !window.confirm("This bank transaction has already been sent to the client. Send again?")) return;
            runBulk("send", "Backend endpoint required: bulk send bank transactions to client.");
          }}
        >
          Send to client
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={selectedVisibleRows.length !== 1 || saving} onClick={() => toggle(selectedVisibleRows[0], "transfer")}>Transfer</Button>
      </BulkActionBar>
      {error ? <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
      {loading ? <div className="mb-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-600">Loading reconciliation lines...</div> : null}
      <div className="overflow-auto rounded-md border border-stone-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
            <tr>
              <th className="w-10 px-2 py-2"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} /></th>
              <th className="w-24 px-2 py-2">Date</th>
              <th className="min-w-72 px-2 py-2">Description</th>
              <th className="px-2 py-2 text-right">Money in</th>
              <th className="px-2 py-2 text-right">Money out</th>
              <th className="min-w-56 px-2 py-2">Supplier/Customer</th>
              <th className="min-w-64 px-2 py-2">Posting treatment</th>
              <th className="min-w-40 px-2 py-2">VAT</th>
              <th className="px-2 py-2">Create transaction</th>
              <th className="px-2 py-2">Match</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length ? pagedTransactions.map((transaction) => {
              const rows = outstandingRowsForBankLine(transaction, outstandingTransactions);
              const isExpanded = expanded?.id === transaction.id;
              const selected = selectedIds.includes(rowId(transaction));
              return (
                <React.Fragment key={transaction.id}>
                  <ReconciliationTransactionRow
                    transaction={transaction}
                    selected={selected}
                    onToggleSelected={() => toggleSelected(transaction)}
                    suppliers={suppliers}
                    customers={customers}
                    postingAccounts={postingAccounts}
                    vatCodes={vatActiveForDate(vatStatus, transactionDate(transaction)) ? vatCodes : vatCodes.filter((option) => String(option.value).toUpperCase() === "NO VAT")}
                    analyticalOptions={analyticalOptions}
                    outsideVatPeriod={!vatActiveForDate(vatStatus, transactionDate(transaction))}
                    createSupplierOrCustomerTransaction={createSupplierOrCustomerTransaction}
                    reconcileToAccount={reconcileToAccount}
                    saving={saving}
                    onMatch={() => toggle(transaction, "match")}
                  />
                  {isExpanded ? (
                    <tr className="border-t border-emerald-100 bg-emerald-50/30">
                      <td colSpan="10" className="px-3 py-3">
                        <ReconciliationInlineWorkflow
                          action={expanded.action}
                          transaction={transaction}
                          outstandingRows={rows}
                          suppliers={suppliers}
                          customers={customers}
                          bankAccounts={bankAccounts}
                          selectedBankId={selectedBankId}
                          postingAccounts={postingAccounts}
                          vatCodes={vatActiveForDate(vatStatus, transactionDate(transaction)) ? vatCodes : vatCodes.filter((option) => String(option.value).toUpperCase() === "NO VAT")}
                          analyticalOptions={analyticalOptions}
                          outsideVatPeriod={!vatActiveForDate(vatStatus, transactionDate(transaction))}
                          matchSuggestion={matchSuggestion}
                          createSupplierOrCustomerTransaction={createSupplierOrCustomerTransaction}
                          reconcileToAccount={reconcileToAccount}
                          reconcileSplitToAccounts={reconcileSplitToAccounts}
                          ignoreTransaction={ignoreTransaction}
                          sendToClientOutstandingItems={sendToClientOutstandingItems}
                          saving={saving}
                          onClose={() => setExpanded(null)}
                        />
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            }) : <tr><td colSpan="10" className="px-3 py-10 text-center text-stone-500">No unreconciled statement lines for this bank account.</td></tr>}
          </tbody>
        </table>
        {transactions.length ? (
          <PaginationFooter
            page={page}
            pageSize={pageSize}
            totalRows={totalRows}
            totalPages={totalPages}
            disabled={loading}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        ) : null}
      </div>
    </Panel>
  );
}

function SearchableInlineSelect({
  value,
  onChange,
  options = [],
  placeholder,
  searchPlaceholder,
  emptyText,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => String(option.value) === String(value || ""));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className="h-9 w-full min-w-52 justify-between gap-2 px-2 text-left text-xs font-normal"
        >
          <span className={`truncate ${selected ? "text-stone-900" : "text-stone-500"}`}>{selected?.label || placeholder}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-stone-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] max-w-[calc(100vw-2rem)] p-0 [&_[cmdk-input-wrapper]]:px-2">
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            className="h-9 text-xs outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={placeholder}
                className="text-stone-900 data-[selected=true]:bg-emerald-50 data-[selected=true]:text-stone-950"
                onSelect={() => {
                  onChange("");
                  setOpen(false);
                }}
              >
                <Check className={`h-4 w-4 ${value ? "opacity-0" : "opacity-100"}`} />
                <span>{placeholder}</span>
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.searchText || ""}`}
                  className="text-stone-900 data-[selected=true]:bg-emerald-50 data-[selected=true]:text-stone-950"
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <Check className={`h-4 w-4 ${String(value || "") === String(option.value) ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ReconciliationTransactionRow({
  transaction,
  selected,
  onToggleSelected,
  suppliers,
  customers,
  postingAccounts,
  vatCodes,
  analyticalOptions,
  outsideVatPeriod,
  createSupplierOrCustomerTransaction,
  reconcileToAccount,
  saving,
  onMatch,
}) {
  const moneyIn = Number(transaction.money_in || 0) > 0;
  const contacts = moneyIn ? customers : suppliers;
  const contactLabel = moneyIn ? "customer" : "supplier";
  const nominalLabel = moneyIn ? "income" : "expense";
  const [contactId, setContactId] = useState(transaction.customer_id || transaction.supplier_id || "");
  const [nominalAccount, setNominalAccount] = useState(transaction.suggested_account_code || transaction.nominal_account_code || "");
  const [vatCode, setVatCode] = useState(transaction.suggested_vat_code || (outsideVatPeriod ? "NO VAT" : ""));
  const [locationId, setLocationId] = useState("");
  const [dimensionId, setDimensionId] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const gross = Math.abs(transactionAmount(transaction));
  const calculatedVat = nominalAccount ? calculateVatInclusiveAmounts(gross, vatCode, vatCodes) : null;
  const vatAmount = calculatedVat?.vat || "0.00";
  const netAmount = calculatedVat?.net || gross.toFixed(2);
  const canonicalVat = nominalAccount ? canonicalVatCodeValue(vatCode, vatCodes) : "";
  const contactOptions = contacts.map((contact) => {
    const id = contact.id || contact.customer_id || contact.supplier_id;
    const code = contact.customer_code || contact.supplier_code || contact.account_code || "";
    const name = contact.name || contact.business_name || contact.trading_name || "";
    return {
      value: id,
      label: [code, name].filter(Boolean).join(" - "),
      searchText: [contact.email, contact.contact_name].filter(Boolean).join(" "),
    };
  });
  const nominalOptions = postingAccounts.map((account) => ({
    value: account.code,
    label: `${account.code} - ${account.name || account.account_name || "Unnamed account"}`,
    searchText: [account.account_type, account.category, account.purpose].filter(Boolean).join(" "),
  }));
  const canCreate = Boolean(nominalAccount || contactId)
    && (!nominalAccount || !vatCodes.length || Boolean(canonicalVat));
  const treatment = contactId
    ? nominalAccount
      ? moneyIn ? "Create sale and allocate receipt" : "Create expense and allocate payment"
      : moneyIn ? "Receipt on account" : "Payment on account"
    : nominalAccount
      ? moneyIn ? "Direct income" : "Direct expense"
      : `Select ${contactLabel} or nominal`;

  useEffect(() => {
    if (!outsideVatPeriod) return;
    setVatCode("NO VAT");
  }, [outsideVatPeriod]);

  async function createTransaction() {
    if (!canCreate) return;
    const common = {
      description: transaction.description || "",
      reference: transaction.reference || transaction.description || "",
      gross_amount: gross.toFixed(2),
      amount: gross.toFixed(2),
      net_amount: nominalAccount ? netAmount : gross.toFixed(2),
      vat_amount: nominalAccount ? vatAmount : "0.00",
      vat_code: nominalAccount ? canonicalVat : "",
      nominal_account_code: nominalAccount,
      default_location_id: locationId || null,
      default_dimension_id: dimensionId || null,
    };
    if (contactId) {
      await createSupplierOrCustomerTransaction(
        transaction,
        {
          ...common,
          allocations: [],
          ...(moneyIn ? { customer_id: contactId } : { supplier_id: contactId }),
        },
        attachment,
      );
      return;
    }
    await reconcileToAccount(transaction, transaction.description, nominalAccount, common);
  }

  return (
    <>
      <tr className={`border-t border-stone-100 align-top hover:bg-emerald-50/40 ${selected ? "bg-emerald-50/70" : ""}`}>
        <td className="px-2 py-2"><input type="checkbox" checked={selected} onChange={onToggleSelected} onClick={(event) => event.stopPropagation()} /></td>
        <td className="whitespace-nowrap px-2 py-2">{formatDate(transactionDate(transaction))}</td>
        <td className="px-2 py-2">
          <div className="font-medium text-stone-900">{transaction.description || "-"}</div>
          <div className="text-xs text-stone-500">{transaction.reference || "No reference"}</div>
        </td>
        <td className="px-2 py-2 text-right text-emerald-700">{Number(transaction.money_in || 0) ? formatMoney(transaction.money_in) : "-"}</td>
        <td className="px-2 py-2 text-right">{Number(transaction.money_out || 0) ? formatMoney(transaction.money_out) : "-"}</td>
        <td className="px-2 py-2">
          <SearchableInlineSelect
            value={contactId}
            onChange={setContactId}
            options={contactOptions}
            placeholder={`No ${contactLabel}`}
            searchPlaceholder={`Search ${contactLabel} code or name...`}
            emptyText={`No ${contactLabel} found.`}
            ariaLabel={`Select ${contactLabel} for ${transaction.description || "bank transaction"}`}
          />
        </td>
        <td className="px-2 py-2">
          <SearchableInlineSelect
            value={nominalAccount}
            onChange={setNominalAccount}
            options={nominalOptions}
            placeholder={contactId ? `${moneyIn ? "Receipt" : "Payment"} on account` : `Select ${nominalLabel} nominal`}
            searchPlaceholder={`Search ${nominalLabel} nominal code or name...`}
            emptyText={`No ${nominalLabel} nominal found.`}
            ariaLabel={`Select ${nominalLabel} nominal for ${transaction.description || "bank transaction"}`}
          />
          <div className="mt-1 text-[11px] leading-tight text-stone-500">{treatment}</div>
        </td>
        <td className="px-2 py-2">
          <select
            value={nominalAccount ? vatCode : ""}
            onChange={(event) => setVatCode(event.target.value)}
            disabled={!nominalAccount}
            aria-label={`Select VAT code for ${transaction.description || "bank transaction"}`}
            className="h-9 w-full min-w-36 rounded-md border border-stone-200 bg-white px-2 text-xs disabled:bg-stone-100 disabled:text-stone-400"
          >
            <option value="">{nominalAccount ? "Select VAT" : "Not applicable"}</option>
            {vatCodes.map((option) => <option key={option.value} value={option.value}>{option.label || option.value}</option>)}
          </select>
          {nominalAccount && canonicalVat ? <div className="mt-1 text-[11px] text-stone-500">VAT {formatMoney(vatAmount)}</div> : null}
        </td>
        <td className="px-2 py-2">
          <div className="flex min-w-32 flex-col gap-1">
            <Button type="button" size="sm" disabled={saving || !canCreate} onClick={createTransaction} style={{ background: "var(--brand)" }}>Create</Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowDetails((current) => !current)}>{showDetails ? "Hide details" : "More details"}</Button>
          </div>
        </td>
        <td className="px-2 py-2"><Button type="button" size="sm" variant="outline" onClick={onMatch}>Match</Button></td>
      </tr>
      {showDetails ? (
        <tr className="border-t border-emerald-100 bg-emerald-50/30">
          <td colSpan="10" className="px-3 py-3">
            <div className="grid gap-3 rounded-md border border-stone-200 bg-white p-3 md:grid-cols-3">
              <BankingAnalyticalFields
                options={analyticalOptions}
                locationId={locationId}
                dimensionId={dimensionId}
                onLocationChange={setLocationId}
                onDimensionChange={setDimensionId}
              />
              <label className="block">
                <span className="text-xs font-semibold text-stone-700">Supporting document</span>
                <input
                  type="file"
                  accept="image/*,application/pdf,.pdf"
                  disabled={!contactId}
                  onChange={(event) => setAttachment(event.target.files?.[0] || null)}
                  className="mt-1 block h-9 w-full rounded-md border border-stone-200 bg-white px-3 py-1.5 text-sm file:mr-3 file:border-0 file:bg-transparent file:text-xs file:font-semibold disabled:bg-stone-100"
                />
                <span className="mt-1 block text-[11px] text-stone-500">
                  {contactId ? attachment?.name || "Optional for the AP/AR transaction." : "Select a supplier/customer to attach a document to its ledger transaction."}
                </span>
              </label>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function ReconciliationInlineWorkflow({ action, transaction, outstandingRows, suppliers, customers, bankAccounts, selectedBankId, postingAccounts, vatCodes, analyticalOptions, outsideVatPeriod, matchSuggestion, createSupplierOrCustomerTransaction, reconcileToAccount, reconcileSplitToAccounts, ignoreTransaction, sendToClientOutstandingItems, saving }) {
  if (action === "match") return <MatchWorkflow transaction={transaction} rows={outstandingRows} matchSuggestion={matchSuggestion} saving={saving} />;
  if (action === "contact") return <SupplierCustomerWorkflow transaction={transaction} suppliers={suppliers} customers={customers} postingAccounts={postingAccounts} vatCodes={vatCodes} analyticalOptions={analyticalOptions} outsideVatPeriod={outsideVatPeriod} createSupplierOrCustomerTransaction={createSupplierOrCustomerTransaction} saving={saving} />;
  if (action === "direct") return <DirectNominalWorkflow transaction={transaction} postingAccounts={postingAccounts} vatCodes={vatCodes} analyticalOptions={analyticalOptions} outsideVatPeriod={outsideVatPeriod} reconcileToAccount={reconcileToAccount} reconcileSplitToAccounts={reconcileSplitToAccounts} saving={saving} />;
  if (action === "transfer") return <TransferWorkflow transaction={transaction} bankAccounts={bankAccounts} selectedBankId={selectedBankId} />;
  if (action === "send") return <SendClientWorkflow transaction={transaction} sendToClientOutstandingItems={sendToClientOutstandingItems} saving={saving} />;
  if (action === "exclude") return <ExcludeWorkflow transaction={transaction} ignoreTransaction={ignoreTransaction} saving={saving} />;
  return null;
}

function MatchWorkflow({ transaction, rows, matchSuggestion, saving }) {
  const [query, setQuery] = useState("");
  const suggestedRows = useMemo(() => outstandingSuggestions(transaction, rows).map((suggestion) => rows.find((row) => String(row.id || row.record_id || "") === suggestionRecordId(suggestion))).filter(Boolean), [rows, transaction]);
  const [selectedIds, setSelectedIds] = useState(() => suggestedRows.map((row) => String(row.id || row.reference)));

  useEffect(() => {
    setSelectedIds(suggestedRows.map((row) => String(row.id || row.reference)));
  }, [suggestedRows]);

  const visibleRows = rows.filter((row) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${accountTransactionContact(row)} ${accountTransactionType(row)} ${row.reference || ""} ${row.document_number || ""} ${row.invoice_number || ""}`.toLowerCase().includes(q);
  });
  const selectedRows = rows.filter((row) => selectedIds.includes(String(row.id || row.reference)));
  const selectedTotal = selectedRows.reduce((total, row) => total + Math.abs(Number(accountTransactionOutstanding(row) || accountTransactionAmount(row) || 0)), 0);
  const bankAmount = Math.abs(transactionAmount(transaction));
  const difference = bankAmount - selectedTotal;
  const validMatch = selectedRows.length > 0 && Math.abs(difference) < 0.01;

  function addSelected(row) {
    const id = String(row.id || row.reference);
    setSelectedIds((current) => current.includes(id) ? current : [...current, id]);
  }

  function removeSelected(row) {
    const id = String(row.id || row.reference);
    setSelectedIds((current) => current.filter((item) => item !== id));
  }

  if (!rows.length) return <InlineBackendMessage message="No outstanding AP/AR transactions available for reconciliation." />;

  return (
    <div className="grid gap-3 rounded-md border border-stone-200 bg-white p-3 xl:grid-cols-[1fr_320px]">
      <div>
        <h4 className="mb-2 text-sm font-semibold text-stone-900">Open transactions ready for allocation</h4>
        <Input className="mb-2 h-9" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search outstanding transactions" />
        <div className="max-h-72 overflow-auto rounded-md border border-stone-200">
          <table className="min-w-full text-left text-xs">
            <thead className="bg-stone-50 uppercase tracking-wide text-stone-500">
              <tr><th className="px-2 py-2">Date</th><th className="px-2 py-2">Type</th><th className="px-2 py-2">Contact/account</th><th className="px-2 py-2">Reference</th><th className="px-2 py-2 text-right">Outstanding</th><th className="px-2 py-2">Action</th></tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.id || row.reference} className={`border-t border-stone-100 ${selectedRows.includes(row) ? "bg-emerald-50" : ""}`}>
                  <td className="whitespace-nowrap px-2 py-2">{formatDate(transactionDate(row))}</td>
                  <td className="px-2 py-2">{accountTransactionType(row) || "-"}</td>
                  <td className="px-2 py-2">{accountTransactionContact(row) || "-"}</td>
                  <td className="px-2 py-2">{row.reference || row.document_number || row.invoice_number || "-"}</td>
                  <td className="px-2 py-2 text-right">{accountTransactionOutstanding(row) !== "" ? formatMoney(accountTransactionOutstanding(row)) : formatMoney(accountTransactionAmount(row))}</td>
                  <td className="px-2 py-2"><Button type="button" size="sm" variant="outline" onClick={() => addSelected(row)}>Add</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h4 className="mb-2 text-sm font-semibold text-stone-900">Selected transactions</h4>
        {selectedRows.length ? (
          <div className="space-y-2">
            {selectedRows.map((row) => (
              <div key={row.id || row.reference} className="rounded-md border border-stone-200 p-2 text-xs">
                <div className="font-semibold text-stone-900">{accountTransactionContact(row) || accountTransactionType(row) || "Selected transaction"}</div>
                <div className="text-stone-500">{row.reference || row.document_number || row.invoice_number || "-"} - {accountTransactionOutstanding(row) !== "" ? formatMoney(accountTransactionOutstanding(row)) : formatMoney(accountTransactionAmount(row))}</div>
                <Button type="button" size="sm" variant="ghost" className="mt-1 h-7 px-0" onClick={() => removeSelected(row)}>Remove</Button>
              </div>
            ))}
          </div>
        ) : <p className="rounded-md border border-dashed border-stone-300 p-3 text-sm text-stone-500">No transactions selected.</p>}
        <WorkflowSummary title="Match total" rows={[["Selected total", formatMoney(selectedTotal)], ["Bank amount", formatMoney(bankAmount)], ["Difference", formatMoney(difference)]]} />
        <Button type="button" className="mt-3 w-full" disabled={saving || !validMatch} onClick={() => matchSuggestion(transaction, selectedRows.map(accountTransactionSuggestion))} style={{ background: "var(--brand)" }}>Confirm match</Button>
      </div>
    </div>
  );
}

function BankingAnalyticalFields({ options = { locations: [], dimensions: [] }, locationId, dimensionId, onLocationChange, onDimensionChange }) {
  return (
    <>
      <label className="block">
        <span className="text-xs font-semibold text-stone-700">Location</span>
        <select value={locationId || ""} onChange={(event) => onLocationChange(event.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm">
          <option value="">No location</option>
          {(options.locations || []).map((row) => <option key={row.id} value={row.id}>{row.code} - {row.name}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-xs font-semibold text-stone-700">Dimension</span>
        <select value={dimensionId || ""} onChange={(event) => onDimensionChange(event.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm">
          <option value="">No dimension</option>
          {(options.dimensions || []).map((row) => <option key={row.id} value={row.id}>{row.dimension_type_name}: {row.code} - {row.name}</option>)}
        </select>
      </label>
    </>
  );
}

function SupplierCustomerWorkflow({ transaction, suppliers, customers, postingAccounts, vatCodes, analyticalOptions, outsideVatPeriod, createSupplierOrCustomerTransaction, saving }) {
  const moneyIn = Number(transaction.money_in || 0) > 0;
  const contactOptions = moneyIn ? customers : suppliers;
  const [contactId, setContactId] = useState(transaction.customer_id || transaction.supplier_id || "");
  const [description, setDescription] = useState(transaction.description || "");
  const [amount, setAmount] = useState(Math.abs(transactionAmount(transaction)));
  const [vatCode, setVatCode] = useState(transaction.suggested_vat_code || "");
  const [vatAmount, setVatAmount] = useState(transaction.suggested_vat_amount || "");
  const [nominalAccount, setNominalAccount] = useState("");
  const [locationId, setLocationId] = useState("");
  const [dimensionId, setDimensionId] = useState("");
  const [attachment, setAttachment] = useState(null);
  const setGrossAmount = (value) => {
    setAmount(value);
    const calculated = calculateVatInclusiveAmounts(value, vatCode, vatCodes);
    if (calculated) setVatAmount(calculated.vat);
  };
  const setSelectedVatCode = (value) => {
    setVatCode(value);
    const calculated = calculateVatInclusiveAmounts(amount, value, vatCodes);
    if (calculated) setVatAmount(calculated.vat);
  };
  useEffect(() => {
    if (outsideVatPeriod) {
      setVatCode("NO VAT");
      setVatAmount("0.00");
    }
  }, [outsideVatPeriod]);
  const gross = Number(amount || 0);
  const vat = Number(vatAmount || 0);
  const canSubmit = Boolean(contactId) && gross > 0 && (!nominalAccount || !vatCodes.length || Boolean(canonicalVatCodeValue(vatCode, vatCodes)));

  function submit() {
    const payload = {
      description,
      reference: transaction.reference || description,
      gross_amount: gross,
      amount: gross,
      net_amount: nominalAccount ? gross - vat : gross,
      vat_amount: nominalAccount ? vat : 0,
      vat_code: nominalAccount ? canonicalVatCodeValue(vatCode, vatCodes) : "",
      nominal_account_code: nominalAccount,
      default_location_id: locationId || null,
      default_dimension_id: dimensionId || null,
      allocations: [],
      ...(moneyIn ? { customer_id: contactId } : { supplier_id: contactId }),
    };
    createSupplierOrCustomerTransaction(transaction, payload, attachment);
  }

  return (
    <div className="space-y-3 rounded-md border border-stone-200 bg-white p-3">
      <div className="text-sm font-semibold text-stone-900">{moneyIn ? "Customer receipt" : "Supplier expense"}</div>
      {outsideVatPeriod && !moneyIn ? <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">Outside VAT registration period. NO VAT applied.</p> : null}
      <div className="grid gap-2 md:grid-cols-4">
        <label className="block">
          <span className="text-xs font-semibold text-stone-700">{moneyIn ? "Customer" : "Supplier"}</span>
          <select value={contactId} onChange={(event) => setContactId(event.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm">
            <option value="">Select {moneyIn ? "customer" : "supplier"}</option>
            {contactOptions.map((contact) => {
              const id = contact.id || contact.customer_id || contact.supplier_id;
              const code = contact.customer_code || contact.supplier_code || contact.account_code || "";
              const name = contact.name || contact.business_name || contact.trading_name || "";
              return <option key={id} value={id}>{[code, name].filter(Boolean).join(" - ")}</option>;
            })}
          </select>
        </label>
        <Field label={moneyIn ? "Description/reference" : "Description"} value={description} onChange={setDescription} />
        <Field label="Gross amount" value={amount} onChange={setGrossAmount} />
        <AccountCodeSelect
          label={`${moneyIn ? "Sales" : "Purchase"} nominal account (optional)`}
          placeholder={moneyIn
            ? "Receipt on account — select nominal to create sale"
            : "Payment on account — select nominal to create expense"}
          accounts={postingAccounts}
          value={nominalAccount}
          onChange={setNominalAccount}
        />
        {nominalAccount ? <VatCodeSelect label="VAT code" value={vatCode} options={vatCodes} onChange={setSelectedVatCode} /> : null}
        {nominalAccount ? <Field label="VAT amount" value={vatAmount} onChange={setVatAmount} /> : null}
        <BankingAnalyticalFields options={analyticalOptions} locationId={locationId} dimensionId={dimensionId} onLocationChange={setLocationId} onDimensionChange={setDimensionId} />
        <label className="block md:col-span-2">
          <span className="text-xs font-semibold text-stone-700">Supporting document (optional)</span>
          <input type="file" accept="image/*,application/pdf,.pdf" onChange={(event) => setAttachment(event.target.files?.[0] || null)} className="mt-1 block h-9 w-full rounded-md border border-stone-200 bg-white px-3 py-1.5 text-sm file:mr-3 file:border-0 file:bg-transparent file:text-xs file:font-semibold" />
          {attachment ? <span className="mt-1 block truncate text-xs text-stone-500">{attachment.name}</span> : null}
        </label>
      </div>
      <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
        {nominalAccount
          ? moneyIn
            ? "A sales item will be created using the selected nominal and VAT code, then this bank receipt will be allocated to it."
            : "A supplier expense will be created using the selected nominal and VAT code, then this bank payment will be allocated to it."
          : moneyIn
            ? "No nominal selected: this will post as a receipt on the customer account only. No sales invoice or invoice value is created; allocate it when the sales document arrives."
            : "No nominal selected: this will post as a payment on the supplier account only. No purchase invoice, expense nominal or invoice value is created; allocate it when the supplier invoice arrives."}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" disabled={saving || !canSubmit} onClick={submit} style={{ background: "var(--brand)" }}>
          {nominalAccount
            ? moneyIn ? "Confirm sales item/receipt" : "Confirm supplier expense/payment"
            : moneyIn ? "Confirm receipt on account" : "Confirm payment on account"}
        </Button>
      </div>
    </div>
  );
}

function DirectNominalWorkflow({ transaction, postingAccounts, vatCodes, analyticalOptions, outsideVatPeriod, reconcileToAccount, reconcileSplitToAccounts, saving }) {
  const moneyIn = Number(transaction.money_in || 0) > 0;
  const [accountCode, setAccountCode] = useState(transaction.suggested_account_code || transaction.nominal_account_code || "");
  const [description, setDescription] = useState(transaction.description || "");
  const [vatCode, setVatCode] = useState(transaction.suggested_vat_code || "");
  const [amount, setAmount] = useState(Math.abs(transactionAmount(transaction)));
  const [vatAmount, setVatAmount] = useState(transaction.suggested_vat_amount || "");
  const [locationId, setLocationId] = useState("");
  const [dimensionId, setDimensionId] = useState("");
  const [splitMode, setSplitMode] = useState(false);
  const [splitLines, setSplitLines] = useState([{ account_code: "", description: transaction.description || "", amount: Math.abs(transactionAmount(transaction)), vat_code: "", vat_amount: "", location_id: "", dimension_id: "" }]);
  useEffect(() => {
    if (!outsideVatPeriod) return;
    setVatCode("NO VAT");
    setVatAmount("0.00");
    setSplitLines((current) => current.map((line) => ({ ...line, vat_code: "NO VAT", vat_amount: "0.00" })));
  }, [outsideVatPeriod]);
  const bankAmount = Math.abs(transactionAmount(transaction));
  const splitTotal = splitLines.reduce((total, line) => total + Number(line.amount || 0), 0);
  const splitBalanced = Math.abs(splitTotal - bankAmount) < 0.01;
  const hasVatCodes = vatCodes.length > 0;
  const setGrossAmount = (value) => {
    setAmount(value);
    const calculated = calculateVatInclusiveAmounts(value, vatCode, vatCodes);
    if (calculated) setVatAmount(calculated.vat);
  };
  const setSelectedVatCode = (value) => {
    setVatCode(value);
    const calculated = calculateVatInclusiveAmounts(amount, value, vatCodes);
    if (calculated) setVatAmount(calculated.vat);
  };

  function updateLine(index, field, value) {
    setSplitLines((current) => current.map((line, lineIndex) => {
      if (lineIndex !== index) return line;
      const next = { ...line, [field]: value };
      if (field !== "amount" && field !== "vat_code") return next;
      const calculated = calculateVatInclusiveAmounts(
        field === "amount" ? value : line.amount,
        field === "vat_code" ? value : line.vat_code,
        vatCodes
      );
      return calculated ? { ...next, vat_amount: calculated.vat } : next;
    }));
  }

  function postSplit() {
    if (!splitLines.every((line) => line.account_code && line.description && line.amount)) return toast.error("Each split line needs nominal account, description and amount.");
    if (!splitBalanced) return toast.error("Split total must equal the bank transaction amount.");
    reconcileSplitToAccounts(transaction, splitLines.map((line) => ({
      ...line,
      gross_amount: line.amount,
      net_amount: (Number(line.amount || 0) - Number(line.vat_amount || 0)).toFixed(2),
      vat_code: canonicalVatCodeValue(line.vat_code, vatCodes),
    })));
  }

  return (
    <div className="space-y-3">
      {outsideVatPeriod ? <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">Outside VAT registration period. NO VAT applied.</p> : null}
      <label className="flex items-center gap-2 text-sm font-semibold text-stone-700"><input type="checkbox" checked={splitMode} onChange={(event) => setSplitMode(event.target.checked)} /> Split transaction</label>
      {!splitMode ? (
        <div className="grid gap-2 md:grid-cols-4">
          <AccountCodeSelect label={moneyIn ? "Income nominal" : "Expense nominal"} accounts={postingAccounts} value={accountCode} onChange={setAccountCode} />
          <Field label="Description" value={description} onChange={setDescription} />
          <VatCodeSelect label="VAT code" value={vatCode} options={vatCodes} onChange={setSelectedVatCode} />
          <Field label="Gross amount" value={amount} onChange={setGrossAmount} />
          <Field label="VAT amount" value={vatAmount} onChange={setVatAmount} />
          <BankingAnalyticalFields options={analyticalOptions} locationId={locationId} dimensionId={dimensionId} onLocationChange={setLocationId} onDimensionChange={setDimensionId} />
          <Button type="button" className="md:col-span-4" disabled={saving || !accountCode || !hasVatCodes} onClick={() => reconcileToAccount(transaction, description, accountCode, { vat_code: canonicalVatCodeValue(vatCode, vatCodes), gross_amount: amount, net_amount: (Number(amount || 0) - Number(vatAmount || 0)).toFixed(2), vat_amount: vatAmount, default_location_id: locationId || null, default_dimension_id: dimensionId || null })} style={{ background: "var(--brand)" }}>Post direct nominal reconciliation</Button>
        </div>
      ) : (
        <div className="space-y-2">
          {splitLines.map((line, index) => (
            <div key={index} className="grid gap-2 rounded-md border border-stone-200 p-2 md:grid-cols-7">
              <AccountCodeSelect label="Nominal account" accounts={postingAccounts} value={line.account_code} onChange={(value) => updateLine(index, "account_code", value)} />
              <Field label="Description" value={line.description} onChange={(value) => updateLine(index, "description", value)} />
              <Field label="Gross amount" value={line.amount} onChange={(value) => updateLine(index, "amount", value)} />
              <VatCodeSelect label="VAT code" value={line.vat_code} options={vatCodes} onChange={(value) => updateLine(index, "vat_code", value)} />
              <Field label="VAT amount" value={line.vat_amount} onChange={(value) => updateLine(index, "vat_amount", value)} />
              <BankingAnalyticalFields options={analyticalOptions} locationId={line.location_id} dimensionId={line.dimension_id} onLocationChange={(value) => updateLine(index, "location_id", value)} onDimensionChange={(value) => updateLine(index, "dimension_id", value)} />
            </div>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-stone-600">
            <span>Split total {formatMoney(splitTotal)} / Bank amount {formatMoney(bankAmount)}</span>
            <Button type="button" size="sm" variant="outline" onClick={() => setSplitLines((current) => [...current, { account_code: "", description: transaction.description || "", amount: "", vat_code: "", vat_amount: "", location_id: "", dimension_id: "" }])}>Add split line</Button>
          </div>
          <Button type="button" disabled={saving || !splitBalanced || !hasVatCodes} onClick={postSplit} style={{ background: "var(--brand)" }}>Post split nominal reconciliation</Button>
        </div>
      )}
    </div>
  );
}

function TransferWorkflow({ transaction, bankAccounts, selectedBankId }) {
  const otherAccounts = bankAccounts.filter((account) => !bankAccountMatchesValue(account, selectedBankId));
  const [otherBankId, setOtherBankId] = useState(transaction.suggested_bank_account_id || transaction.transfer_bank_account_id || otherAccounts[0]?.id || "");
  const [reference, setReference] = useState(transaction.reference || "");
  const [description, setDescription] = useState(transaction.description || "");
  const amount = Math.abs(transactionAmount(transaction));
  return (
    <div className="grid gap-2 rounded-md border border-stone-200 bg-white p-3 md:grid-cols-5">
      <BankAccountSelect label="Other bank account" bankAccounts={otherAccounts} value={otherBankId} onChange={setOtherBankId} />
      <Field label="Reference" value={reference} onChange={setReference} />
      <Field label="Description" value={description} onChange={setDescription} />
      <Field label="Amount" value={amount} onChange={() => {}} />
      <div className="pt-5"><Button type="button" variant="outline" className="w-full">Confirm transfer</Button></div>
      <InlineBackendMessage message="Backend endpoint required: bank transfer reconciliation." />
    </div>
  );
}

function SendClientWorkflow({ transaction, sendToClientOutstandingItems, saving }) {
  const [documentType, setDocumentType] = useState("Supporting document");
  return (
    <div className="grid gap-3 rounded-md border border-stone-200 bg-white p-3 md:grid-cols-[1fr_240px_auto]">
      <div>
        <div className="text-sm font-semibold text-stone-900">Send this bank transaction to the client outstanding items list?</div>
        <div className="mt-1 text-xs text-stone-500">{transaction.description || "Bank transaction"} - {formatMoney(Math.abs(transactionAmount(transaction)))}</div>
      </div>
      <SelectField label="Document type" value={documentType} onChange={setDocumentType} options={["Supporting document", "Purchase invoice", "Sales invoice", "Receipt", "Unknown"]} />
      <div className="pt-5"><Button type="button" disabled={saving} onClick={() => sendToClientOutstandingItems(transaction, { document_type: documentType })} style={{ background: "var(--brand)" }}>Send to client</Button></div>
    </div>
  );
}

function ExcludeWorkflow({ transaction, ignoreTransaction, saving }) {
  const [reason, setReason] = useState("");
  return (
    <div className="grid gap-2 rounded-md border border-stone-200 bg-white p-3 md:grid-cols-[1fr_auto]">
      <Field label="Reason" value={reason} onChange={setReason} />
      <div className="pt-5"><Button type="button" variant="outline" disabled={saving} onClick={() => ignoreTransaction(transaction, reason)}>Confirm exclude</Button></div>
      <p className="text-xs text-stone-500 md:col-span-2">Excluded lines disappear from Reconciliation after backend confirms and remain visible in Bank Statements as excluded/ignored.</p>
    </div>
  );
}

function WorkflowSummary({ title, rows }) {
  return (
    <div className="rounded-md border border-stone-200 p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">{title}</div>
      <div className="grid gap-1 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3"><span className="text-stone-500">{label}</span><span className="text-right font-medium text-stone-900">{value}</span></div>
        ))}
      </div>
    </div>
  );
}

function InlineBackendMessage({ message }) {
  return <div className="rounded-md border border-dashed border-stone-300 bg-stone-50 p-3 text-sm text-stone-600 md:col-span-full">{message}</div>;
}

function BulkActionBar({ selectedCount, onClear, message, children }) {
  if (!selectedCount && !message) return null;
  return (
    <div className="mb-3 rounded-md border border-stone-200 bg-stone-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-stone-900">{selectedCount} selected</div>
        <div className="flex flex-wrap gap-2">
          {children}
          {selectedCount ? <Button type="button" size="sm" variant="ghost" onClick={onClear}>Clear selection</Button> : null}
        </div>
      </div>
      {message ? <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{message}</div> : null}
    </div>
  );
}

function BankStatementsTab({ imports, statementLines, selectedBank, selectedImportId, setSelectedImportId, selectedImportLines, statusFilter, setStatusFilter, totalRows, totalPages, page, pageSize, loading, error, onPageChange, onPageSizeChange, importFile, setImportFile, importStatement, downloadStatementTemplate, bulkAction, busy }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMessage, setBulkMessage] = useState("");
  const sourceLines = selectedImportId ? selectedImportLines : statementLines;
  const linesToShow = sourceLines;
  const pagedLines = linesToShow;
  const unreconciledCount = statementLines.filter(isReconciliationStatus).length;
  const duplicateKeys = duplicateStatementKeys(sourceLines);
  const selectedVisibleRows = selectedRowsFromIds(pagedLines, selectedIds);
  const pagedLineIds = useMemo(() => pagedLines.map(rowId).filter(Boolean), [pagedLines]);
  const allVisibleSelected = pagedLineIds.length > 0 && pagedLineIds.every((id) => selectedIds.includes(id));
  const selectedHasReconciled = selectedVisibleRows.some((line) => ["reconciled", "matched"].includes(normaliseStatus(line.status)));
  const selectedCanUnreconcile = selectedVisibleRows.some((line) => ["reconciled", "matched"].includes(normaliseStatus(line.status)));
  const selectedHasNonReconciliation = selectedVisibleRows.some((line) => !isReconciliationStatus(line));
  const currentBalance = Number(selectedBank?.current_balance || 0);
  const noStatementLinesMessage = imports.length
    ? "Imported batches exist, but no statement rows are visible for this bank. Backend bank identity mapping may need review."
    : currentBalance
      ? `Current balance comes from posted accounting entries on nominal ${bankCode(selectedBank) || "this bank account"}. No bank statement lines have been imported for this bank account yet.`
      : "No bank transactions found.";

  useEffect(() => {
    setSelectedIds([]);
    setBulkMessage("");
  }, [selectedImportId, statusFilter, statementLines]);

  function toggleSelected(row) {
    const id = rowId(row);
    setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  function toggleAll() {
    setSelectedIds((current) => {
      if (allVisibleSelected) return current.filter((id) => !pagedLineIds.includes(id));
      return Array.from(new Set([...current, ...pagedLineIds]));
    });
  }

  async function deleteSelected() {
    setBulkMessage("");
    if (selectedHasReconciled) {
      setBulkMessage("Reconciled statement lines must be unreconciled before they can be deleted.");
      return;
    }
    if (selectedHasNonReconciliation && !window.confirm("This statement line is not currently in Reconciliation. Delete from Bank Statements only?")) return;
    const result = await bulkAction("delete", selectedVisibleRows, "Backend endpoint required: bulk delete bank statement lines.");
    if (result?.ok) setSelectedIds([]);
    if (result?.message) setBulkMessage(result.message);
  }

  async function unreconcileSelected() {
    setBulkMessage("");
    const result = await bulkAction("unreconcile", selectedVisibleRows, "Backend endpoint required: bulk unreconcile bank statement lines.");
    if (result?.ok) setSelectedIds([]);
    if (result?.message) setBulkMessage(result.message);
  }

  return (
    <div className="grid gap-4 xl:grid-cols-[1fr_380px]">
      <Panel title="Bank statement lines">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-stone-900">Full statement reflection</div>
          <div className="flex items-center gap-2">
            <select value={statusFilter || "all"} onChange={(event) => setStatusFilter(event.target.value)} className="h-8 rounded-md border border-stone-200 bg-white px-2 text-sm shadow-sm">
              <option value="all">All</option>
              <option value="unreconciled">Unreconciled</option>
              <option value="reconciled">Reconciled</option>
              <option value="excluded">Excluded</option>
            </select>
            <Badge className="bg-amber-100 text-amber-800">{unreconciledCount} unreconciled</Badge>
          </div>
        </div>
        <BulkActionBar selectedCount={selectedVisibleRows.length} onClear={() => setSelectedIds([])} message={selectedHasReconciled ? "Reconciled statement lines must be unreconciled before they can be deleted." : bulkMessage}>
          <Button type="button" size="sm" variant="outline" disabled={!selectedVisibleRows.length || selectedHasReconciled || busy} onClick={deleteSelected}>Delete</Button>
          <Button type="button" size="sm" variant="outline" disabled={!selectedVisibleRows.length || !selectedCanUnreconcile || busy} onClick={unreconcileSelected}>Unreconcile</Button>
        </BulkActionBar>
        {error ? <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
        {loading ? <div className="mb-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-600">Loading bank statement lines...</div> : null}
        <BankTransactionTable
          transactions={pagedLines}
          selectable
          selectedIds={selectedIds}
          allSelected={allVisibleSelected}
          onToggleAll={toggleAll}
          onToggleRow={toggleSelected}
          duplicateKeys={duplicateKeys}
          emptyMessage={noStatementLinesMessage}
        />
        {linesToShow.length ? (
          <PaginationFooter
            page={page}
            pageSize={pageSize}
            totalRows={totalRows}
            totalPages={totalPages}
            disabled={loading}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        ) : null}
      </Panel>
      <div className="space-y-4">
        <Panel title="Import statement">
          <form onSubmit={importStatement} className="space-y-3">
            <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
              <p className="font-semibold">CSV format for {selectedBank?.account_name || selectedBank?.name || "this bank account"}</p>
              <p className="mt-1">Maximum 2 MB. Required: Date and either a signed Amount column, or Money In / Money Out columns. Optional: Description, Reference and Balance. Dates may be YYYY-MM-DD or DD/MM/YYYY.</p>
            </div>
            <Button type="button" variant="outline" className="w-full gap-2" disabled={busy || !selectedBank?.id} onClick={downloadStatementTemplate}><Download className="h-4 w-4" /> Download CSV template</Button>
            <Input type="file" accept=".csv,text/csv" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
            <Button disabled={busy || !importFile} className="w-full gap-2" style={{ background: "var(--brand)" }}><Upload className="h-4 w-4" /> Import statement</Button>
            <p className="text-xs text-stone-500">Imported rows appear here. Unreconciled rows also appear in Reconciliation.</p>
          </form>
        </Panel>
        <Panel title="Import batches">
        <div className="overflow-auto rounded-md border border-stone-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
              <tr><th className="px-3 py-2">Import date</th><th className="px-3 py-2">Statement range</th><th className="px-3 py-2">Source</th><th className="px-3 py-2 text-right">Opening</th><th className="px-3 py-2 text-right">Closing</th><th className="px-3 py-2 text-right">Rows</th><th className="px-3 py-2 text-right">Reconciled</th><th className="px-3 py-2 text-right">Unreconciled</th><th className="px-3 py-2 text-right">Duplicates/skipped</th></tr>
            </thead>
            <tbody>
              {imports.length ? imports.map((item) => (
                <tr key={item.id} onClick={() => setSelectedImportId(item.id)} className={`cursor-pointer border-t border-stone-100 hover:bg-emerald-50/50 ${selectedImportId === item.id ? "bg-emerald-50/60" : ""}`}>
                  <td className="px-3 py-2">{formatDateTime(item.created_at || item.imported_at || item.import_date)}</td>
                  <td className="px-3 py-2">{[formatDate(item.statement_start_date), formatDate(item.statement_end_date)].filter((value) => value !== "-").join(" - ") || "-"}</td>
                  <td className="px-3 py-2">{item.source_type || item.provider || "CSV/manual"}</td>
                  <td className="px-3 py-2 text-right">{item.opening_balance !== undefined ? formatMoney(item.opening_balance) : "-"}</td>
                  <td className="px-3 py-2 text-right">{item.closing_balance !== undefined ? formatMoney(item.closing_balance) : "-"}</td>
                  <td className="px-3 py-2 text-right">{item.rows_imported ?? item.row_count ?? "-"}</td>
                  <td className="px-3 py-2 text-right">{item.rows_reconciled ?? item.reconciled_rows ?? "-"}</td>
                  <td className="px-3 py-2 text-right">{item.rows_unreconciled ?? item.unreconciled_rows ?? "-"}</td>
                  <td className="px-3 py-2 text-right">{item.duplicates ?? item.skipped_rows ?? item.duplicates_skipped ?? "-"}</td>
                </tr>
              )) : <tr><td colSpan="9" className="px-3 py-10 text-center text-stone-500">No statement imports for this bank account.</td></tr>}
            </tbody>
          </table>
        </div>
        {selectedImportId ? <Button type="button" variant="outline" className="mt-3 w-full" onClick={() => setSelectedImportId("")}>Show all statement lines</Button> : null}
        </Panel>
      </div>
    </div>
  );
}

function AccountTransactionsTab({ filters, setFilters, transactions, allTransactions, totalRows, totalPages, page, pageSize, loading, error, onPageChange, onPageSizeChange, bankAccounts, bulkDelete, busy, exportTransactions }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMessage, setBulkMessage] = useState("");
  const moduleOptions = Array.from(new Set(allTransactions.map(accountTransactionModule).filter(Boolean)));
  const typeOptions = Array.from(new Set(allTransactions.map(accountTransactionType).filter(Boolean)));
  const statusOptions = Array.from(new Set(allTransactions.map((item) => item.status).filter(Boolean)));
  const pagedTransactions = transactions;
  const pagedTransactionIds = useMemo(() => pagedTransactions.map(rowId).filter(Boolean), [pagedTransactions]);
  const selectedVisibleRows = selectedRowsFromIds(pagedTransactions, selectedIds);
  const allVisibleSelected = pagedTransactionIds.length > 0 && pagedTransactionIds.every((id) => selectedIds.includes(id));

  useEffect(() => {
    setSelectedIds([]);
    setBulkMessage("");
  }, [transactions]);

  function toggleSelected(row) {
    const id = rowId(row);
    setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  function toggleAll() {
    setSelectedIds((current) => {
      if (allVisibleSelected) return current.filter((id) => !pagedTransactionIds.includes(id));
      return Array.from(new Set([...current, ...pagedTransactionIds]));
    });
  }

  async function deleteSelected() {
    setBulkMessage("");
    const result = await bulkDelete(selectedVisibleRows);
    if (result?.ok) setSelectedIds([]);
    if (result?.message) setBulkMessage(result.message);
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-stone-200 bg-white p-3">
        <div className="mb-3 text-sm font-semibold text-stone-900">All outstanding account transactions</div>
        <div className="grid gap-2 md:grid-cols-4 xl:grid-cols-8">
          <BankAccountSelect label="Bank account" bankAccounts={bankAccounts} value={filters.bank_account_id} onChange={(value) => setFilters((current) => ({ ...current, bank_account_id: value }))} includeAll />
          <SelectField label="Module" value={filters.module} onChange={(value) => setFilters((current) => ({ ...current, module: value }))} options={["", ...moduleOptions]} />
          <SelectField label="Type" value={filters.type} onChange={(value) => setFilters((current) => ({ ...current, type: value }))} options={["", ...typeOptions]} />
          <Field label="Contact/account" value={filters.contact} onChange={(value) => setFilters((current) => ({ ...current, contact: value }))} />
          <Field label="Date from" type="date" value={filters.date_from} onChange={(value) => setFilters((current) => ({ ...current, date_from: value }))} />
          <Field label="Date to" type="date" value={filters.date_to} onChange={(value) => setFilters((current) => ({ ...current, date_to: value }))} />
          <SelectField label="Status" value={filters.status} onChange={(value) => setFilters((current) => ({ ...current, status: value }))} options={["", ...statusOptions]} />
          <div>
            <Label className="text-xs font-semibold text-stone-600">Search</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
              <Input className="h-9 pl-9" value={filters.search} onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))} placeholder="Search" />
            </div>
          </div>
          <div className="mt-5 flex gap-2">
            <Button type="button" variant="outline" className="gap-2" onClick={exportTransactions}><Download className="h-4 w-4" /> Export</Button>
            <Button type="button" variant="outline" className="gap-2" onClick={() => window.print()}><Printer className="h-4 w-4" /> Print</Button>
          </div>
        </div>
      </div>
      <Panel title="All outstanding account transactions">
        <BulkActionBar selectedCount={selectedVisibleRows.length} onClear={() => setSelectedIds([])} message={bulkMessage}>
          <Button type="button" size="sm" variant="outline" disabled={!selectedVisibleRows.length || busy} onClick={deleteSelected}>Delete</Button>
        </BulkActionBar>
        {error ? <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
        {loading ? <div className="mb-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-600">Loading outstanding account transactions...</div> : null}
        <AccountTransactionTable
          transactions={pagedTransactions}
          selectable
          selectedIds={selectedIds}
          allSelected={allVisibleSelected}
          onToggleAll={toggleAll}
          onToggleRow={toggleSelected}
        />
        {transactions.length ? (
          <PaginationFooter
            page={page}
            pageSize={pageSize}
            totalRows={totalRows}
            totalPages={totalPages}
            disabled={loading}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        ) : null}
      </Panel>
    </div>
  );
}

function transactionDate(transaction) {
  return transaction?.transaction_date || transaction?.date || transaction?.posted_at || transaction?.created_at || "";
}

function rowId(row) {
  return String(row?.id || row?.record_id || row?.reference || row?.journal_reference || `${transactionDate(row)}-${row?.description || ""}-${row?.money_in || ""}-${row?.money_out || ""}`);
}

function selectedRowsFromIds(rows, selectedIds) {
  const ids = new Set(selectedIds);
  return rows.filter((row) => ids.has(rowId(row)));
}

function bankTransactionIdentity(row) {
  return {
    id: row?.id,
    bank_account_id: row?.bank_account_id,
    account_id: row?.account_id,
    bank_id: row?.bank_id,
    bank_account_code: row?.bank_account_code,
    account_code: row?.account_code,
    nominal_account_code: row?.nominal_account_code,
    status: row?.status,
  };
}

function accountTransactionDeleteIdentity(row) {
  return {
    module: accountTransactionModule(row),
    type: accountTransactionType(row),
    id: row?.id || row?.record_id,
  };
}

function isSentToClient(row) {
  const status = normaliseStatus(row?.status);
  return status === "documentation_requested" || status === "sent_to_client" || row?.sent_to_client === true || row?.client_outstanding_item_id;
}

function statementDuplicateKey(row) {
  return [
    transactionDate(row),
    normaliseText(row?.description),
    Number(row?.money_in || 0).toFixed(2),
    Number(row?.money_out || 0).toFixed(2),
  ].join("|");
}

function duplicateStatementKeys(rows) {
  const counts = rows.reduce((map, row) => {
    const key = statementDuplicateKey(row);
    map.set(key, (map.get(key) || 0) + 1);
    return map;
  }, new Map());
  return new Set(Array.from(counts.entries()).filter(([, count]) => count > 1).map(([key]) => key));
}

function importResultCounts(response = {}) {
  const errors = Array.isArray(response.errors) ? response.errors.length : Number(response.errors || response.error_count || 0);
  return {
    imported: Number(response.imported ?? response.rows_imported ?? response.imported_rows ?? 0),
    duplicates: Number(response.duplicates ?? response.duplicate_rows ?? response.rows_duplicate ?? response.skipped_duplicates ?? response.skipped_rows ?? 0),
    errors,
    errorSummary: Array.isArray(response.errors) ? response.errors.filter(Boolean).slice(0, 3).join("; ") : response.error_summary || response.message || "",
  };
}

function showImportResult(result) {
  if (result.imported > 0) {
    toast.success(`Bank statement imported: ${result.imported} rows imported.`);
    return;
  }
  if (result.duplicates > 0) {
    toast.info(`No new rows imported. ${result.duplicates} duplicate rows skipped.`);
    return;
  }
  if (result.errors > 0) {
    toast.warning(result.errorSummary || `${result.errors} rows could not be imported.`);
    return;
  }
  toast.warning("No bank statement rows were imported. Check the CSV format.");
}

function bankName(account) {
  return account?.account_name || account?.name || account?.accountName || "";
}

function bankCode(account) {
  return account?.nominal_account_code || account?.code || account?.account_code || "";
}

function bankAccountMatchesValue(account, value) {
  const target = String(value || "").trim();
  if (!target) return false;
  return [
    account?.id,
    account?.bank_account_id,
    account?.account_id,
    account?.bank_id,
    account?.nominal_account_code,
    account?.code,
    account?.account_code,
  ].some((candidate) => String(candidate || "").trim() === target);
}

function isBankAccountType(account) {
  return account?.purpose === "Bank Account" || String(account?.account_type || account?.type || "").toLowerCase() === "bank";
}

function hasBankingFlag(account) {
  return account?.show_in_banking === true || account?.banking_enabled === true;
}

function isBankingEnabledAccount(account) {
  return account?.active !== false && isBankAccountType(account) && hasBankingFlag(account);
}

function accountTransactionModule(transaction) {
  return transaction?.module || transaction?.ledger_module || transaction?.source_module || transaction?.destination || "";
}

function accountTransactionType(transaction) {
  return transaction?.type || transaction?.transaction_type || transaction?.document_type || transaction?.record_type || "";
}

function accountTransactionContact(transaction) {
  return transaction?.contact_name || transaction?.customer_name || transaction?.supplier_name || transaction?.account_name || transaction?.contact || "";
}

function accountTransactionAmount(transaction) {
  return transaction?.amount ?? transaction?.gross_amount ?? transaction?.total ?? transaction?.balance ?? "";
}

function accountTransactionOutstanding(transaction) {
  return transaction?.outstanding_amount ?? transaction?.unallocated_amount ?? transaction?.remaining_amount ?? transaction?.amount_outstanding ?? "";
}

function transactionAmount(transaction) {
  const amount = Number(transaction?.money_in || 0) - Number(transaction?.money_out || 0);
  if (amount) return amount;
  return Number(accountTransactionOutstanding(transaction) || accountTransactionAmount(transaction) || 0);
}

function normaliseText(value) {
  return String(value || "").trim().toLowerCase();
}

function isApOutstandingTransaction(transaction) {
  const module = normaliseText(accountTransactionModule(transaction));
  const type = normaliseText(accountTransactionType(transaction));
  return module === "ap" || module.includes("payable") || type.includes("purchase") || type.includes("supplier");
}

function isArOutstandingTransaction(transaction) {
  const module = normaliseText(accountTransactionModule(transaction));
  const type = normaliseText(accountTransactionType(transaction));
  return module === "ar" || module.includes("receivable") || type.includes("sales") || type.includes("customer");
}

function outstandingRowsForBankLine(transaction, outstandingRows) {
  const moneyIn = Number(transaction?.money_in || 0) > 0;
  const direction = moneyIn ? "money_in" : "money_out";
  return outstandingRows.filter((item) => item?.match_eligible !== false && (item?.direction ? item.direction === direction : moneyIn ? isArOutstandingTransaction(item) : isApOutstandingTransaction(item)));
}

function accountTransactionSuggestion(transaction) {
  return {
    id: transaction.id,
    account_transaction_id: transaction.account_transaction_id || transaction.id,
    record_id: transaction.linked_record_id || transaction.record_id || transaction.id,
    record_type: transaction.linked_record_type || transaction.type || accountTransactionType(transaction),
    match_type: transaction.linked_record_type || transaction.type || accountTransactionType(transaction),
    type: transaction.linked_record_type || transaction.type || accountTransactionType(transaction),
    label: accountTransactionLabel(transaction),
    contact_name: accountTransactionContact(transaction),
    reference: transaction.reference || transaction.document_number || transaction.invoice_number,
    amount: accountTransactionOutstanding(transaction) || accountTransactionAmount(transaction),
  };
}

function accountTransactionLabel(transaction) {
  return [accountTransactionContact(transaction), transaction.reference || transaction.document_number || transaction.invoice_number, accountTransactionType(transaction)]
    .filter(Boolean)
    .join(" - ") || "Outstanding transaction";
}

function suggestedBankMatchLabel(transaction) {
  const suggestion = transaction?.suggested_bank_match || transaction?.suggested_match;
  if (!suggestion) return "-";
  if (typeof suggestion === "string") return suggestion;
  return suggestion.label || suggestion.description || suggestion.reference || "Suggested match available";
}

function suggestionRecordId(suggestion) {
  return String(suggestion?.record_id || suggestion?.id || "");
}

function suggestionRecordType(suggestion) {
  return normaliseText(suggestion?.type || suggestion?.record_type);
}


function outstandingSuggestions(transaction, outstandingRows) {
  const rawSuggestions = Array.isArray(transaction?.suggestions) ? transaction.suggestions : [];
  if (!rawSuggestions.length || !outstandingRows.length) return [];
  return rawSuggestions.reduce((matches, suggestion) => {
    const row = outstandingRows.find((item) => {
      const sameId = String(item.id || item.record_id || "") === suggestionRecordId(suggestion);
      const type = suggestionRecordType(suggestion);
      const sameType = !type || normaliseText(accountTransactionType(item)) === type || normaliseText(item.record_type) === type;
      return sameId && sameType;
    });
    if (row) {
      matches.push({
        ...suggestion,
        id: row.id,
        record_id: row.id || suggestion.record_id,
        type: suggestion.type || accountTransactionType(row) || row.record_type,
        record_type: suggestion.record_type || accountTransactionType(row) || row.record_type,
        label: accountTransactionLabel(row),
        contact_name: accountTransactionContact(row),
        reference: row.reference || row.document_number || row.invoice_number,
        amount: accountTransactionOutstanding(row) || accountTransactionAmount(row),
      });
    }
    return matches;
  }, []);
}

function normaliseStatus(status) {
  return String(status || "unreconciled").trim().toLowerCase();
}

function displayStatus(status) {
  const value = normaliseStatus(status);
  if (["pending", "awaiting_match", "unmatched", "imported"].includes(value)) return "Unreconciled";
  if (value === "documentation_requested") return "Documentation requested";
  if (value === "matched") return "Matched";
  if (value === "ignored" || value === "excluded") return "Excluded";
  if (value === "reconciled") return "Reconciled";
  return value ? value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) : "Unreconciled";
}

function isReconciliationStatus(transaction) {
  return reconciliationStatuses.has(normaliseStatus(transaction?.status));
}

function importLines(item, transactions) {
  if (Array.isArray(item?.lines)) return item.lines;
  const id = item?.id;
  return transactions.filter((transaction) => transaction.import_id === id || transaction.statement_import_id === id);
}

function BankAccountSelect({ bankAccounts = [], value, onChange, label = "Bank account", includeAll = false }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-stone-600">{label}</Label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm shadow-sm">
        <option value="">{includeAll ? "All bank accounts" : "Select bank account"}</option>
        {bankAccounts.map((account) => <option key={account.id || bankCode(account)} value={account.id}>{bankName(account)} - {bankCode(account)}</option>)}
      </select>
    </div>
  );
}

function BankTransactionTable({ transactions = [], emptyMessage = "No bank transactions found.", selectable = false, selectedIds = [], allSelected = false, onToggleAll, onToggleRow, duplicateKeys = new Set() }) {
  if (!transactions.length) return <p className="py-10 text-center text-sm text-stone-500">{emptyMessage}</p>;
  return (
    <div className="overflow-auto rounded-md border border-stone-200">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
          <tr>
            {selectable ? <th className="w-10 px-3 py-2"><input type="checkbox" checked={allSelected} onChange={onToggleAll} /></th> : null}
            <th className="px-3 py-2">Date</th>
            <th className="px-3 py-2">Description</th>
            <th className="px-3 py-2">Reference</th>
            <th className="px-3 py-2 text-right">Money in</th>
            <th className="px-3 py-2 text-right">Money out</th>
            <th className="px-3 py-2 text-right">Balance</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Matched / linked record</th>
            <th className="px-3 py-2">Journal reference</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((transaction) => {
            const selected = selectedIds.includes(rowId(transaction));
            const duplicate = duplicateKeys.has(statementDuplicateKey(transaction));
            return (
              <tr key={transaction.id || transaction.reference} className={`border-t border-stone-100 ${selected ? "bg-emerald-50/70" : duplicate ? "bg-amber-50/50" : ""}`}>
                {selectable ? <td className="px-3 py-2"><input type="checkbox" checked={selected} onChange={() => onToggleRow(transaction)} /></td> : null}
                <td className="whitespace-nowrap px-3 py-2">{formatDate(transactionDate(transaction))}</td>
                <td className="max-w-96 truncate px-3 py-2 font-medium text-stone-900">{transaction.description || "-"}</td>
                <td className="px-3 py-2 text-stone-600">{transaction.reference || "-"}</td>
                <td className="px-3 py-2 text-right text-emerald-700">{Number(transaction.money_in || 0) ? formatMoney(transaction.money_in) : "-"}</td>
                <td className="px-3 py-2 text-right">{Number(transaction.money_out || 0) ? formatMoney(transaction.money_out) : "-"}</td>
                <td className="px-3 py-2 text-right">{transaction.running_balance !== undefined ? formatMoney(transaction.running_balance) : transaction.balance !== undefined ? formatMoney(transaction.balance) : "-"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge className={statusBadgeClass(normaliseStatus(transaction.status))}>{displayStatus(transaction.status)}</Badge>
                    {duplicate ? <Badge className="bg-amber-100 text-amber-800">Possible duplicate</Badge> : null}
                  </div>
                </td>
                <td className="px-3 py-2 text-stone-600">{transaction.linked_record || transaction.matched_to || transaction.suggested_match || "-"}</td>
                <td className="px-3 py-2 text-stone-600">{transaction.journal_reference || transaction.journal_id || "-"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AccountTransactionTable({ transactions = [], selectable = false, selectedIds = [], allSelected = false, onToggleAll, onToggleRow }) {
  if (!transactions.length) {
    return (
      <div className="rounded-md border border-dashed border-stone-300 bg-stone-50 p-8 text-center text-sm text-stone-600">
        No outstanding AP/AR transactions available for reconciliation.
      </div>
    );
  }
  return (
    <div className="overflow-auto rounded-md border border-stone-200">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
          <tr>
            {selectable ? <th className="w-10 px-3 py-2"><input type="checkbox" checked={allSelected} onChange={onToggleAll} /></th> : null}
            <th className="px-3 py-2">Date</th>
            <th className="px-3 py-2">Module</th>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Contact/account</th>
            <th className="px-3 py-2">Reference</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2 text-right">Outstanding / unallocated</th>
            <th className="px-3 py-2">Suggested bank match</th>
            <th className="px-3 py-2">Reconciliation status</th>
            <th className="px-3 py-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((transaction) => {
            const selected = selectedIds.includes(rowId(transaction));
            return (
            <tr key={transaction.id || transaction.reference} className={`border-t border-stone-100 ${selected ? "bg-emerald-50/70" : ""}`}>
              {selectable ? <td className="px-3 py-2"><input type="checkbox" checked={selected} onChange={() => onToggleRow(transaction)} /></td> : null}
              <td className="whitespace-nowrap px-3 py-2">{formatDate(transactionDate(transaction))}</td>
              <td className="px-3 py-2 text-stone-600">{accountTransactionModule(transaction) || "-"}</td>
              <td className="px-3 py-2 font-medium text-stone-900">{accountTransactionType(transaction) || "-"}</td>
              <td className="px-3 py-2 text-stone-600">{accountTransactionContact(transaction) || "-"}</td>
              <td className="px-3 py-2 text-stone-600">{transaction.reference || transaction.document_number || transaction.invoice_number || "-"}</td>
              <td className="px-3 py-2 text-right">{accountTransactionAmount(transaction) !== "" ? formatMoney(accountTransactionAmount(transaction)) : "-"}</td>
              <td className="px-3 py-2 text-right">{accountTransactionOutstanding(transaction) !== "" ? formatMoney(accountTransactionOutstanding(transaction)) : "-"}</td>
              <td className="px-3 py-2 text-stone-600">{suggestedBankMatchLabel(transaction)}</td>
              <td className="px-3 py-2"><Badge className={statusBadgeClass(normaliseStatus(transaction.status))}>{displayStatus(transaction.status)}</Badge></td>
              <td className="px-3 py-2">
                {transaction.view_url || transaction.match_supported ? (
                  <Button type="button" size="sm" variant="outline" onClick={() => toast.info("Backend endpoint required: account transaction view/match action.")}>{transaction.match_supported ? "Match" : "View"}</Button>
                ) : "-"}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function exportRows(rows, filename) {
  if (!rows?.length) {
    toast.info("There are no rows to export yet.");
    return;
  }
  const keys = Array.from(rows.reduce((set, row) => {
    Object.keys(row || {}).forEach((key) => {
      if (typeof row[key] !== "object") set.add(key);
    });
    return set;
  }, new Set()));
  const csv = [keys.join(","), ...rows.map((row) => keys.map((key) => `"${String(row[key] ?? "").replaceAll("\"", "\"\"")}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.replace(/\s+/g, "-").toLowerCase();
  link.click();
  URL.revokeObjectURL(url);
}

export default BankingWorkspace;
